import { DateTime } from 'luxon';
import { dom } from './dom.js';
import { apiFetch, uploadPhoto, bulkUploadPhotos, getPhotosForTrack, deletePhoto, updatePhotoCaption, getSharedPhotos, getSharedTripPhotos, getSharedNotes, getSharedTripNotes, saveNote, deleteNote, getNotesForTrack, boatAgent, updatePersonProfile, listTrackCrew, listTripCrew, addCrew, removeCrew, updateCrew, importData, getCharterCompanies, getCruisingRegions, listCredentials, createCredential, updateCredential, deleteCredentialById, getCredentialOrganizations, getCredentialNames } from './api.js';
import { apiUrl, defaultTrackColors } from './config.js';
import { formatTrackDateRange, getIconForWeather, degrees, isValidFeetInchesFormat, feetInchesToInches, inchesToFeetInches, countDecimalPlaces, captureAndExport, getTimezoneFromLatLon } from './utils.js';
import {
    drawTrackOnMap, removeTrackFromMap, updateMapBounds, updateMarkersVisibility, clearAllMapLayers, makeDraggable, drawPhotoIcons, removePhotoIcons, drawNoteIcons, removeNoteIcons, initializeMap, setAllPhotoLayersVisibility, setAllNoteLayersVisibility
} from './map.js';
import {
    mapInstance, draw, visibleTrackIds, trackDataCache, notesByTrackId, boatDataCache, activeTrip, currentUser,
    setDrawingMode, isDrawingMode, setTrimPopup, trimPopup, setActiveTrip, clearTrackState, resetColorIndex, setCurrentUser,
    photosVisible, notesVisible, setPhotosVisible, setNotesVisible
} from './state.js';
import { renderLandingPage } from './pages/landing.js';
import { initDiagram, refreshDiagram, renderReadOnlyDiagram } from './diagram.js';

let previousUrl = '/';

let captionBlurHandler = null;

async function getStats() {
  return apiFetch('/api/v1/me/stats');
}

export function updateUIForLoggedInState(user) {
    setCurrentUser(user);
    if (dom.loginModal) dom.loginModal.classList.remove("show");
    if (dom.landingPageContainer) dom.landingPageContainer.innerHTML = '';
    if (dom.appContent) dom.appContent.classList.remove("hidden");
    if (dom.loadingOverlay) dom.loadingOverlay.classList.remove("show");
    if (dom.userInfoDisplay) dom.userInfoDisplay.classList.remove("hidden");
    if (dom.userAvatar) {
        dom.userAvatar.src = user.picture_url || '/profile_placeholder.svg';
        dom.userAvatar.alt = user.name ? `${user.name}'s avatar` : 'User avatar';
        dom.userAvatar.title = 'View Profile';
    }

    if (user.is_admin && dom.adminControls) {
        dom.adminControls.classList.remove("hidden");
    } else if (dom.adminControls) {
        dom.adminControls.classList.add("hidden");
    }

    const path = window.location.pathname;
    const isSpaPage = path === '/' || path.endsWith('index.html') || path.startsWith('/track') || path.startsWith('/trip') || path.startsWith('/boat');

    if (mapInstance) {
        initializeMap();
    } else if (isSpaPage) {
        initializeMap();
    }
    
    if (isSpaPage) {
        getStats().then(renderStats).catch(error => {
            console.warn("Failed to fetch stats:", error);
            const statsContainer = document.getElementById('stats-container');
            if (statsContainer) statsContainer.classList.add('hidden');
        });

        getTracks().catch(error => {
            console.warn("Failed to fetch tracks:", error);
        });

        getTrips().catch(error => {
            console.warn("Failed to fetch trips:", error);
        });

        getBoats().catch(error => {
            console.warn("Failed to fetch boats:", error);
        });

        handleUrlPath();
    }
}

function handleUrlPath() {
    const path = window.location.pathname;

    // Share URLs (/track/share/:id, /trip/share/:id) are handled by the map's
    // style.load handler via handleSharedView. Skip track-ID parsing so the
    // literal "share" segment isn't mistaken for a track ID.
    if (getShareIdFromUrl(path)) {
        return;
    }

    const parts = path.split('/').filter(p => p);

    if (parts.length > 0) {
        const type = parts[0];
        const ids = parts.slice(1);

        if (type === 'track' && ids.length > 0) {
            showTracks(ids);
        } else if (type === 'trip' && ids.length === 1) {
            showTrip(ids[0]);
        } else if (type === 'boat' && ids.length === 1) {
            showBoat(ids[0]);
        }
    }
}

export function showBoat(boatId) {
    // Switch to the boats tab
    const boatTab = document.querySelector('.tab-button[data-tab="boats"]');
    if (boatTab) {
        boatTab.click();
    }
    showBoatView(boatId);
}

export function renderStats(stats) {
    if (!stats || !stats.total_distance) {
        const statsContainer = document.getElementById('stats-container');
        if (statsContainer) statsContainer.classList.add('hidden');
        return;
    }
    const statsContainer = document.getElementById('stats-container');
    if (!statsContainer) return;

    const statItems = [];

     statItems.push(`<div class="stat-group">`)

     if (stats.most_used_boat) {
        statItems.push(`
        <div class="stat-item boat">
            <span class="stat-value">${stats.most_used_boat}</span>
            <span class="stat-label">Most Used Boat</span>
        </div>`);
    }

    // General stats
    if (stats.total_distance) {
        statItems.push(`
        <div class="stat-item distance">
            <span class="stat-value">${stats.total_distance.toFixed(0)}</span>
            <span class="stat-label">Total Nautical Miles</span>
        </div>`);
    }

    

    if (stats.formatted_duration) {
        statItems.push(`
        <div class="stat-item time">
            <span class="stat-value">${stats.formatted_duration}</span>
            <span class="stat-label">Total Time Underway</span>
        </div>`);
    }
     statItems.push(`</div>`)
    statItems.push(`<div class="stat-group-divider"></div>`);

    statItems.push(`<div class="stat-group">`)
    // Track stats
    if (stats.total_tracks) {
        statItems.push(`
        <div class="stat-item count">
            <span class="stat-value">${stats.total_tracks}</span>
            <span class="stat-label">Tracks</span>
        </div>`);
    }

    if (stats.longest_track_by_distance && stats.longest_track_by_distance.name) {
        statItems.push(`
        <div class="stat-item distance">
            <span class="stat-value">${stats.longest_track_by_distance.value.toFixed(0)} nm</span>
            <span class="stat-label">Longest Track (Distance)</span>
            <span class="stat-sublabel">${stats.longest_track_by_distance.name}</span>
        </div>`);
    }

    if (stats.longest_track_by_duration && stats.longest_track_by_duration.name) {
        statItems.push(`
        <div class="stat-item time">
            <span class="stat-value">${stats.longest_track_by_duration.formatted_duration}</span>
            <span class="stat-label">Longest Track (Time Underway)</span>
            <span class="stat-sublabel">${stats.longest_track_by_duration.name}</span>
        </div>`);
    }
     statItems.push(`</div>`)
    statItems.push(`<div class="stat-group-divider"></div>`);
     statItems.push(`<div class="stat-group">`)

    // Trip stats
    if (stats.total_trips) {
        statItems.push(`
        <div class="stat-item count">
            <span class="stat-value">${stats.total_trips}</span>
            <span class="stat-label">Trips</span>
        </div>`);
    }

    if (stats.longest_trip_by_distance && stats.longest_trip_by_distance.name) {
        statItems.push(`
        <div class="stat-item distance">
            <span class="stat-value">${stats.longest_trip_by_distance.value.toFixed(0)} nm</span>
            <span class="stat-label">Longest Trip (Distance)</span>
            <span class="stat-sublabel">${stats.longest_trip_by_distance.name}</span>
        </div>`);
    }

    if (stats.longest_trip_by_duration && stats.longest_trip_by_duration.name) {
        statItems.push(`
        <div class="stat-item time">
            <span class="stat-value">${stats.longest_trip_by_duration.formatted_duration}</span>
            <span class="stat-label">Longest Trip (Time Underway)</span>
            <span class="stat-sublabel">${stats.longest_trip_by_duration.name}</span>
        </div>`);
    }

    statsContainer.innerHTML = `
        <div class="stats-handle">
            <span class="material-symbols-outlined">unfold_less</span>
        </div>
        ${statItems.join('')}
    `;
    statsContainer.classList.remove('hidden');
}

export function updateUIForLoggedOutState() {
    if (dom.adminControls) {
        dom.adminControls.classList.add("hidden");
    }
    const share = getShareIdFromUrl(window.location.pathname);
    if (share) {
        handleSharedView(share);
    } else {
        renderLandingPage();
    }
}

export function getShareIdFromUrl(pathname) {
    const pathParts = pathname.split('/');
    if (pathParts.length === 4 && (pathParts[1] === 'track' || pathParts[1] === 'trip') && pathParts[2] === 'share') {
        return { type: pathParts[1], id: pathParts[3] };
    }
    return null;
}

export async function handleSharedView(share) {
    if (!mapInstance) {
        initializeMap();
        return;
    }

    try {
        const data = await apiFetch(`/api/v1/shared/${share.type}/${share.id}`);
        if (data) {
            dom.loginModal.classList.remove("show");
            dom.appContent.classList.remove("hidden");
            dom.authControls.classList.add("hidden");
            dom.tracksPanel.classList.add('hidden');

            if (mapInstance) {
                mapInstance.resize();
            }
            
            if (share.type === 'track') {
                visibleTrackIds.add(data.id);
                trackDataCache[data.id] = data;
                await drawTrackOnMap(data);
                getSharedPhotos(share.id).then(photos => {
                    if (photos && photos.length > 0) {
                        drawPhotoIcons(data.id, photos);
                    }
                });
                getSharedNotes(share.id).then(notes => {
                    if (notes && notes.length > 0) {
                        notesByTrackId[data.id] = notes;
                        drawNoteIcons(data.id, notes);
                    }
                });
            } else {
                const drawPromises = data.tracks.map(async (track, index) => {
                    const trackIdStr = track.id.toString();
                    visibleTrackIds.add(trackIdStr);
                    track.color = defaultTrackColors[index % defaultTrackColors.length];
                    trackDataCache[trackIdStr] = track;
                    await drawTrackOnMap(track);
                });
                await Promise.all(drawPromises);
                getSharedTripPhotos(share.id).then(photos => {
                    if (photos && photos.length > 0) {
                        const photosByTrack = photos.reduce((acc, photo) => {
                            if (!acc[photo.track_id]) {
                                acc[photo.track_id] = [];
                            }
                            acc[photo.track_id].push(photo);
                            return acc;
                        }, {});
                        for (const trackId in photosByTrack) {
                            drawPhotoIcons(trackId, photosByTrack[trackId]);
                        }
                    }
                });
                getSharedTripNotes(share.id).then(notes => {
                    if (notes && notes.length > 0) {
                        const notesByTrack = notes.reduce((acc, note) => {
                            if (!acc[note.track_id]) {
                                acc[note.track_id] = [];
                            }
                            acc[note.track_id].push(note);
                            return acc;
                        }, {});
                        for (const trackId in notesByTrack) {
                            notesByTrackId[trackId] = notesByTrack[trackId];
                            drawNoteIcons(trackId, notesByTrack[trackId]);
                        }
                    }
                });
            }

            updateMapBounds();
            updateMarkersVisibility();
            updateLegend();

            mapInstance.once('idle', () => {
                if (share.type === 'trip') {
                    const tripTitleEl = dom.multiTrackView.querySelector('.trip-title');
                    const tripDescriptionEl = dom.multiTrackView.querySelector('.trip-description');
                    if (tripTitleEl) {
                        tripTitleEl.textContent = data.name;
                        tripTitleEl.contentEditable = false;
                    }
                    if (tripDescriptionEl) {
                        tripDescriptionEl.innerHTML = (data.description || '').replace(/\n/g, "<br>");
                        tripDescriptionEl.contentEditable = false;
                        tripDescriptionEl.removeAttribute('data-placeholder');
                    }
                    const createTripBtn = dom.multiTrackView.querySelector('.create-trip-btn');
                    if (createTripBtn) createTripBtn.style.display = 'none';

                    const tripActions = dom.multiTrackView.querySelector('.trip-actions');
                    if(tripActions) tripActions.style.display = 'none';
                } else { // it's a track
                    dom.legendTitle.contentEditable = false;
                    dom.legendDescriptionText.contentEditable = false;
                    dom.legendDescriptionText.removeAttribute('data-placeholder');
                    if (!data.description) {
                        dom.legendDescriptionContainer.classList.add('hidden');
                    }
                }

                dom.legendTitle.removeEventListener("blur", saveTrackDetails);
                dom.legendDescriptionText.removeEventListener("blur", saveTrackDetails);


                if (dom.shareTrackButton) {
                    dom.shareTrackButton.style.display = 'none';
                    dom.shareTrackButton.removeEventListener("click", handleShareClick);
                }
                if (dom.toggleLegendButton) {
                    dom.toggleLegendButton.style.display = 'block';
                    dom.toggleLegendButton.addEventListener("click", toggleLegend);
                }
            });
        } else {
            showError({ message: `The shared ${share.type} could not be found.` });
            renderLandingPage();
        }
    } catch (error) {
        showError(error);
        renderLandingPage();
    }
}

function getTracks() {
    return apiFetch(apiUrl).then((data) => {
        if (!dom.tracksList || !dom.trackActions) return;

        dom.tracksList.innerHTML = "";

        if (Array.isArray(data) && data.length > 0) {
            // Sort tracks by start date descending
            data.sort((a, b) => new Date(b.start) - new Date(a.start));

            const fragment = document.createDocumentFragment();
            let currentYear = null;

            data.forEach(track => {
                const trackYear = new Date(track.start).getFullYear();
                if (trackYear !== currentYear) {
                    currentYear = trackYear;
                    const divider = document.createElement('li');
                    divider.className = 'year-divider';
                    divider.textContent = currentYear;
                    fragment.appendChild(divider);
                }
                fragment.appendChild(createTrackListItem(track));
            });
            dom.tracksList.appendChild(fragment);
        } else {
            const li = document.createElement("li");
            li.className = "empty-list-item";
            li.innerHTML = `<a><span class="label empty-list-label">No tracks available.</span></a>`;
            dom.tracksList.appendChild(li);
            clearAllMapLayers();
            if (mapInstance) mapInstance.flyTo({ center: [0, 0], zoom: 1.5 });

            // Show Getting Started Modal if no tracks
            if (dom.gettingStartedModal && !localStorage.getItem('gettingStartedDismissed')) {
                dom.gettingStartedModal.classList.add('show');
            }
        }
        updateLegend();
    });
}

function createTrackListItem(track) {
    const canDelete = currentUser && currentUser.id === track.owner_id;
    const isShared = currentUser && currentUser.id !== track.owner_id;

    const deleteBtnHtml = canDelete ? `
      <button class="delete-track-btn" data-track-id="${track.id}" data-track-name="${track.name}" title="Delete track: ${track.name}">
        <span class="material-symbols-outlined">delete</span>
      </button>` : '';

    const sharedIconHtml = isShared ? `<span class="material-symbols-outlined shared-icon" title="Shared with you">group</span>` : '';

    const li = document.createElement('li');
    li.innerHTML = `
    <div class="track-item-container">
      <input type="checkbox" class="track-toggle" id="track-toggle-${track.id}" data-track-id="${track.id}">
      <label for="track-toggle-${track.id}" class="track-label-group">
        <div class="track-name-row">
             ${sharedIconHtml}
             <span class="label">${track.name}</span>
        </div>
        <span class="date">${formatTrackDateRange(track.start, track.end, track.weather_report ? track.weather_report.time_zone : undefined)}</span>
      </label>
      ${deleteBtnHtml}
    </div>`;

    let weatherIcon = document.createElement('span');
    weatherIcon.className = 'material-symbols-outlined track-list-icon list-weather';
    weatherIcon.textContent = track.weather_report ? getIconForWeather(track.weather_report.description) : 'cloud';
    li.querySelector('.track-label-group').insertAdjacentElement('beforeend', weatherIcon);

    if (track.weather_report && track.weather_report.wind) {
        const dirWind = parseInt(track.weather_report.wind.direction);
        const rotation = (dirWind + 180) % 360;

        let windIcon = document.createElement('span');
        windIcon.className = 'material-symbols-outlined track-list-icon list-wind';
        windIcon.textContent = "navigation";
        windIcon.style.transform = `rotate(${rotation}deg)`
        li.querySelector('.track-label-group').insertAdjacentElement('beforeend', windIcon);
    }

    if (track.boat) {
        let boatIcon = document.createElement('span');
        boatIcon.className = 'material-symbols-outlined track-list-icon has-boat';
        boatIcon.textContent = track.boat.type === "Monohull" ? 'sailing' : 'directions_boat';
        li.querySelector('.track-label-group').insertAdjacentElement('beforeend', boatIcon);
    }

    li.querySelector('.track-toggle').addEventListener('change', handleTrackToggle);
    const deleteBtn = li.querySelector('.delete-track-btn');
    if (deleteBtn) {
        deleteBtn.addEventListener('click', promptForDelete);
    }
    return li;
}

function collapseTracksPanelOnMobile() {
    if (window.innerWidth <= 768 && !dom.tracksPanel.classList.contains('collapsed')) {
        toggleTracksPanel();
    }
}

function handleTrackToggle(e) {
    const cb = e.currentTarget;
    const id = cb.dataset.trackId;
    const isChecked = cb.checked;
    cb.closest('li').classList.toggle('current', isChecked);

    const updateUrl = () => {
        if (activeTrip) return;
        if (visibleTrackIds.size > 0) {
            const url = '/track/' + [...visibleTrackIds].join('/');
            history.pushState(null, '', url);
        } else {
            history.pushState(null, '', '/');
        }
    };

    if (isChecked) {
        apiFetch(`${apiUrl}/${id}?with_points=true`).then(async data => {
            visibleTrackIds.add(id);
            trackDataCache[id] = data;
            await drawTrackOnMap(data);
            getPhotosForTrack(id).then(photos => {
                if (photos && photos.length > 0) {
                    drawPhotoIcons(id, photos);
                }
            });
            getNotesForTrack(id).then(notes => {
                if (notes && notes.length > 0) {
                    notesByTrackId[id] = notes;
                    drawNoteIcons(id, notes);
                }
            });
            refreshUI();
            updateUrl();
            collapseTracksPanelOnMobile();
        }).catch(error => {
            showError(error);
            cb.checked = false;
            cb.closest('li').classList.remove('current');
        });
    } else {
        visibleTrackIds.delete(id);
        delete trackDataCache[id];
        removeTrackFromMap(id);
        removePhotoIcons(id);
        removeNoteIcons(id);
        if (visibleTrackIds.size === 1) {
            const [remainingId] = visibleTrackIds;
            const remainingTrack = trackDataCache[remainingId];
            if (remainingTrack && remainingTrack.color !== defaultTrackColors[0]) {
                remainingTrack.color = defaultTrackColors[0];
                if (mapInstance.getLayer(`line-layer-${remainingId}`)) {
                    mapInstance.setPaintProperty(`line-layer-${remainingId}`, 'line-color', remainingTrack.color);
                }
            }
        }
        updateUrl();
        refreshUI();
    }
}

function saveTrackDetails() {
    const trackId = dom.legendTitle.dataset.trackId;
    if (!trackId) return showError({ message: "Could not find track ID to save." });

    const description = dom.legendDescriptionText.innerText;
    const name = dom.legendTitle.textContent;

    apiFetch(`${apiUrl}/${trackId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, description }),
    }).then(updatedTrack => {
        if (trackDataCache[trackId]) {
            trackDataCache[trackId].name = updatedTrack.name;
            trackDataCache[trackId].description = updatedTrack.description;
        }
        getTracks();
    }).catch(showError);
}

function addTrack(e) {
    e.preventDefault();
    dom.uploadUI.classList.add("show");
    dom.uploadStoreInput.disabled = false;
    dom.uploadSubmitButton.disabled = true;
    dom.uploadFileInput.value = null;
    dom.fileNameDisplay.textContent = "No file chosen";
}

function enableUpload(e) {
    const file = e.target.files[0];
    if (file) {
        dom.uploadSubmitButton.disabled = false;
        dom.fileNameDisplay.textContent = file.name;
    }
}

function processUpload(e) {
    e.preventDefault();
    const form = e.currentTarget;
    const url = form.action;
    const formData = new FormData(form);

    dom.uploadForm.style.display = "none";
    dom.loadingMessage.style.visibility = "visible";

    apiFetch(url, {
        method: "POST",
        body: formData
    }).then(newTracks => {
        hideUpload();
        getTracks().then(() => {
            if (newTracks && newTracks.length > 0) {
                const newTrackId = newTracks[0].id;
                const trackToggle = document.getElementById(`track-toggle-${newTrackId}`);
                if (trackToggle) {
                    clearAllSelections();
                    trackToggle.checked = true;
                    trackToggle.dispatchEvent(new Event('change', { bubbles: true }));
                    trackToggle.closest('li').scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
        });
    }).catch(error => {
        showError(error);
        dom.uploadForm.style.display = "flex";
    }).finally(() => {
        dom.loadingMessage.style.visibility = "hidden";
    });
}

function hideUpload() {
    if (dom.uploadUI) {
        dom.uploadUI.classList.remove("show");
        dom.uploadForm.reset();
        dom.uploadForm.style.display = "flex";
        dom.uploadSubmitButton.disabled = true;
        dom.fileNameDisplay.textContent = "No file chosen";
    }
}

function addImport(e) {
    e.preventDefault();
    if (dom.importUI) dom.importUI.classList.add("show");
    if (dom.importSubmitButton) dom.importSubmitButton.disabled = true;
    if (dom.importFileInput) dom.importFileInput.value = null;
    if (dom.importFileNameDisplay) dom.importFileNameDisplay.textContent = "No file chosen";
    if (dom.importResult) {
        dom.importResult.textContent = "";
        dom.importResult.classList.remove("import-success", "import-error");
    }
    if (dom.importForm) dom.importForm.style.display = "flex";
    if (dom.importLoadingMsg) dom.importLoadingMsg.style.visibility = "hidden";
}

function enableImportUpload(e) {
    const file = e.target.files[0];
    if (file) {
        if (dom.importSubmitButton) dom.importSubmitButton.disabled = false;
        if (dom.importFileNameDisplay) dom.importFileNameDisplay.textContent = file.name;
    }
}

async function processImport(e) {
    e.preventDefault();
    const formData = new FormData(dom.importForm);

    dom.importForm.style.display = "none";
    if (dom.importLoadingMsg) dom.importLoadingMsg.style.visibility = "visible";

    try {
        const result = await importData(formData);
        const parts = [];
        if (result.boats_imported) parts.push(`${result.boats_imported} boat${result.boats_imported !== 1 ? 's' : ''}`);
        if (result.trips_imported) parts.push(`${result.trips_imported} trip${result.trips_imported !== 1 ? 's' : ''}`);
        if (result.tracks_imported) parts.push(`${result.tracks_imported} track${result.tracks_imported !== 1 ? 's' : ''}`);
        if (result.notes_imported) parts.push(`${result.notes_imported} note${result.notes_imported !== 1 ? 's' : ''}`);
        if (result.crew_imported) parts.push(`${result.crew_imported} crew member${result.crew_imported !== 1 ? 's' : ''}`);
        if (result.photos_imported) parts.push(`${result.photos_imported} photo${result.photos_imported !== 1 ? 's' : ''}`);

        if (dom.importResult) {
            dom.importResult.textContent = `Imported: ${parts.join(', ') || 'nothing'}`;
            dom.importResult.classList.add("import-success");
        }
        if (dom.importOkBtn) dom.importOkBtn.classList.remove("hidden");

        getTracks();
        getTrips();
        getBoats();
    } catch (error) {
        showError(error);
        dom.importForm.style.display = "flex";
    } finally {
        if (dom.importLoadingMsg) dom.importLoadingMsg.style.visibility = "hidden";
    }
}

function hideImport() {
    if (dom.importUI) {
        dom.importUI.classList.remove("show");
        if (dom.importForm) {
            dom.importForm.reset();
            dom.importForm.style.display = "flex";
        }
        if (dom.importSubmitButton) dom.importSubmitButton.disabled = true;
        if (dom.importFileNameDisplay) dom.importFileNameDisplay.textContent = "No file chosen";
        if (dom.importResult) {
            dom.importResult.textContent = "";
            dom.importResult.classList.remove("import-success", "import-error");
        }
        if (dom.importOkBtn) dom.importOkBtn.classList.add("hidden");
    }
}

function handleToggleMarkers() {
    const newVisible = !photosVisible;
    setPhotosVisible(newVisible);
    setNotesVisible(newVisible);
    setAllPhotoLayersVisibility(newVisible);
    setAllNoteLayersVisibility(newVisible);
    const icon = 'visibility' + (newVisible ? '' : '_off');
    [dom.toggleMarkersBtn, dom.multiTrackView && dom.multiTrackView.querySelector('.toggle-markers-btn')]
        .filter(Boolean)
        .forEach(btn => {
            const span = btn.querySelector('.material-symbols-outlined');
            if (span) span.textContent = icon;
        });
}

function createTripListItem(trip) {
    const canDelete = currentUser && currentUser.id === trip.owner_id;
    const isShared = currentUser && currentUser.id !== trip.owner_id;
    
    const deleteBtnHtml = canDelete ? `
                      <button class="delete-trip-btn" data-trip-id="${trip.id}" data-trip-name="${trip.name}" title="Delete trip: ${trip.name}">
                          <span class="material-symbols-outlined">delete</span>
                      </button>` : '';

    const sharedIconHtml = isShared ? `<span class="material-symbols-outlined shared-icon" title="Shared with you">group</span>` : '';

    const li = document.createElement('li');
    li.className = 'trip-list-item';
    li.dataset.tripId = trip.id;
    li.innerHTML = `<div class="trip-item-container">
                      <span class="material-symbols-outlined trip-icon">folder_open</span>
                      <label for="trip-toggle-${trip.id}" class="trip-label-group">
                          <div class="trip-name-row">
                            ${sharedIconHtml}
                            <span class="label">${trip.name}</span>
                          </div>
                          <span class="date">${formatTrackDateRange(trip.start, trip.end, trip.time_zone)}</span>
                      </label>
                      ${deleteBtnHtml}
                  </div>`;
    li.addEventListener('click', handleTripClick);
    const deleteBtn = li.querySelector('.delete-trip-btn');
    if (deleteBtn) {
        deleteBtn.addEventListener('click', promptForDelete);
    }
    return li;
}

async function getTrips() {
    const trips = await apiFetch('/api/v1/trip');
    if (!dom.tripsList) return;

    dom.tripsList.innerHTML = "";

    if (Array.isArray(trips) && trips.length > 0) {
        // Sort trips by start date descending
        trips.sort((a, b) => new Date(b.start) - new Date(a.start));

        const fragment = document.createDocumentFragment();
        let currentYear = null;

        trips.forEach(trip => {
            const tripYear = new Date(trip.start).getFullYear();
            if (tripYear !== currentYear) {
                currentYear = tripYear;
                const divider = document.createElement('li');
                divider.className = 'year-divider';
                divider.textContent = currentYear;
                fragment.appendChild(divider);
            }
            fragment.appendChild(createTripListItem(trip));
        });
        dom.tripsList.appendChild(fragment);
    } else {
        const li = document.createElement("li");
        li.className = "empty-list-item";
        li.innerHTML = `<a><span class="label empty-list-label">No trips available.</span></a>`;
        dom.tripsList.appendChild(li);
    }
}

function handleTripClick(e) {
    const li = e.currentTarget;
    const tripId = li.dataset.tripId;
    if (!tripId) return;

    history.pushState(null, '', '/trip/' + tripId);

    clearAllSelections();

    document.querySelectorAll('.trips-list .trip-list-item').forEach(item => item.classList.remove('current'));
    li.classList.add('current');

    apiFetch(`/api/v1/trip/${tripId}`).then(tripData => {
        if (!tripData || !tripData.tracks || tripData.tracks.length === 0) {
            updateLegend();
            return;
        }
        setActiveTrip(tripData);

        tripData.tracks.forEach((track, index) => {
            const trackIdStr = track.id.toString();
            visibleTrackIds.add(trackIdStr);
            track.color = defaultTrackColors[index % defaultTrackColors.length];
            trackDataCache[trackIdStr] = track;
            drawTrackOnMap(track);
            getPhotosForTrack(trackIdStr).then(photos => {
                if (photos && photos.length > 0) {
                    drawPhotoIcons(trackIdStr, photos);
                }
            });
            getNotesForTrack(trackIdStr).then(notes => {
                if (notes && notes.length > 0) {
                    notesByTrackId[trackIdStr] = notes;
                    drawNoteIcons(trackIdStr, notes);
                }
            });
        });

        updateLegend();
        mapInstance.once('idle', () => {
            updateMapBounds();
            updateMarkersVisibility();
        });
        collapseTracksPanelOnMobile();
    }).catch(error => {
        showError(error);
        updateLegend();
        mapInstance.flyTo({ center: [0, 0], zoom: 1.5 });
    });
}

function createTrip(e) {
    e.preventDefault();
    const tripNameEl = document.querySelector('.multi-track-view .trip-title');
    const tripDescriptionEl = document.querySelector('.multi-track-view .trip-description');
    const tripName = tripNameEl.textContent;
    const tripDescription = tripDescriptionEl.innerText;

    if (!tripName) return showError({ message: "Trip name cannot be empty." });

    const tracksForTrip = [...visibleTrackIds].map(id => ({ id: parseInt(id, 10) }));
    const tripData = { name: tripName, description: tripDescription, tracks: tracksForTrip };

    apiFetch('/api/v1/trip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tripData)
    }).then(() => {
        getTrips();
        const saveBtn = document.querySelector('.create-trip-btn');
        const originalText = saveBtn.textContent;
        saveBtn.textContent = 'Saved!';
        saveBtn.disabled = true;
        setTimeout(() => {
            saveBtn.textContent = originalText;
            saveBtn.disabled = false;
        }, 2000);
    }).catch(showError);
}

export function updateTripDetails() {
    if (!activeTrip) return;

    const tripTitleEl = dom.multiTrackView.querySelector('.trip-title');
    const tripDescriptionEl = dom.multiTrackView.querySelector('.trip-description');
    const newName = tripTitleEl.textContent;
    const newDescription = tripDescriptionEl.innerText;

    if (newName === activeTrip.name && newDescription === (activeTrip.description || '')) return;

    const tripData = {
        id: activeTrip.id,
        name: newName,
        description: newDescription,
        tracks: activeTrip.tracks.map(t => ({ id: t.id })),
        start: activeTrip.start,
        end: activeTrip.end,
    };

    apiFetch(`/api/v1/trip/${activeTrip.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tripData)
    }).then(updatedTrip => {
        activeTrip.name = updatedTrip.name;
        activeTrip.description = updatedTrip.description;
        const tripListItem = dom.tripsList.querySelector(`.trip-list-item[data-trip-id="${activeTrip.id}"] .label`);
        if (tripListItem) tripListItem.textContent = updatedTrip.name;
    }).catch(showError);
}

function createBoatListItem(boat) {
    const canDelete = currentUser && currentUser.id === boat.owner_id;
    const deleteBtnHtml = canDelete ? `
                      <button class="delete-boat-btn" data-boat-id="${boat.id}" data-boat-name="${boat.name}" title="Delete boat: ${boat.name}">
                          <span class="material-symbols-outlined">delete</span>
                      </button>` : '';

    const li = document.createElement('li');
    let icon = boat.type === "Monohull" ? 'sailing' : 'directions_boat';

    li.className = 'boat-list-item';
    li.dataset.boatId = boat.id;
    li.style.cursor = 'pointer';
    li.title = `View details for ${boat.name}`;
    li.innerHTML = `<div class="boat-item-container">
                      <span class="material-symbols-outlined boat-icon">${icon}</span>
                      <div class="trip-label-group">
                          <span class="label">${boat.name}</span>
                          <span class="date">${[boat.make, boat.model, boat.year].filter(Boolean).join(' ')}</span>
                      </div>
                      ${deleteBtnHtml}
                  </div>`;

    li.addEventListener('click', e => {
        if (!e.target.closest('.delete-boat-btn')) {
            history.pushState(null, '', '/boat/' + boat.id);
            showBoatView(boat.id);
        }
    });
    const deleteBtn = li.querySelector('.delete-boat-btn');
    if (deleteBtn) {
        deleteBtn.addEventListener('click', promptForDelete);
    }
    return li;
}

export async function showBoatView(boatId) {
    try {
        const boat = await apiFetch(`/api/v1/boat/${boatId}`);
        const boatDetails = dom.boatViewDetails;
        const title = dom.boatViewModal.querySelector('.modal-title');

        title.textContent = boat.name;

        boatDetails.innerHTML = `
            <div class="detail-item">
                <span class="detail-label">Type</span>
                <span class="detail-value">${boat.type || 'N/A'}</span>
            </div>
            <div class="detail-item">
                <span class="detail-label">Make</span>
                <span class="detail-value">${boat.make || 'N/A'}</span>
            </div>
            <div class="detail-item">
                <span class="detail-label">Model</span>
                <span class="detail-value">${boat.model || 'N/A'}</span>
            </div>
            <div class="detail-item">
                <span class="detail-label">Year</span>
                <span class="detail-value">${boat.year || 'N/A'}</span>
            </div>
            <div class="detail-item">
                <span class="detail-label">Length</span>
                <span class="detail-value">${inchesToFeetInches(boat.length) || 'N/A'}</span>
            </div>
            <div class="detail-item">
                <span class="detail-label">Beam</span>
                <span class="detail-value">${inchesToFeetInches(boat.beam) || 'N/A'}</span>
            </div>
            <div class="detail-item">
                <span class="detail-label">Draft</span>
                <span class="detail-value">${inchesToFeetInches(boat.draft) || 'N/A'}</span>
            </div>
            <div class="detail-item">
                <span class="detail-label">Mast</span>
                <span class="detail-value">${inchesToFeetInches(boat.mast) || 'N/A'}</span>
            </div>
            <div class="detail-item">
                <span class="detail-label">Displacement</span>
                <span class="detail-value">${boat.displacement ? `${boat.displacement.toLocaleString()} lbs` : 'N/A'}</span>
            </div>
            <div class="detail-item">
                <span class="detail-label">Charter</span>
                <span class="detail-value">${boat.is_charter ? 'Yes' : 'No'}</span>
            </div>
            ${boat.is_charter ? `
            <div class="detail-item">
                <span class="detail-label">Charter Company</span>
                <span class="detail-value">${boat.charter_company || 'N/A'}</span>
            </div>
            <div class="detail-item">
                <span class="detail-label">Cruising Region</span>
                <span class="detail-value">${boat.cruising_region || 'N/A'}</span>
            </div>` : ''}
            <div class="detail-item span-2">
                <span class="detail-label">Notes</span>
                <span class="detail-value">${boat.notes || 'N/A'}</span>
            </div>
        `;

        if (currentUser && currentUser.id === boat.owner_id) {
            dom.editBoatBtn.style.display = 'inline-block';
            dom.editBoatBtn.onclick = () => {
                dom.boatViewModal.classList.remove('show');
                showBoatForm(boatId);
            };
        } else {
            dom.editBoatBtn.style.display = 'none';
        }

        const readSvgContainer = document.getElementById('read_svg_container');
        if (readSvgContainer) {
            renderReadOnlyDiagram(boat, readSvgContainer);
        }

        dom.boatViewModal.classList.add('show');
    } catch (error) {
        showError(error);
    }
}

async function getBoats() {
    const boats = await apiFetch('/api/v1/boat');
    if (!dom.boatsList) return;

    dom.boatsList.innerHTML = "";
    Object.keys(boatDataCache).forEach(key => delete boatDataCache[key]);

    if (Array.isArray(boats) && boats.length > 0) {
        const fragment = document.createDocumentFragment();
        boats.forEach(boat => {
            boatDataCache[boat.id] = boat;
            fragment.appendChild(createBoatListItem(boat));
        });
        dom.boatsList.appendChild(fragment);
    } else {
        const li = document.createElement("li");
        li.className = "empty-list-item";
        li.innerHTML = `<a><span class="label empty-list-label">No boats available.</span></a>`;
        dom.boatsList.appendChild(li);
    }
}

function populateDatalist(datalistId, values) {
    const dl = document.getElementById(datalistId);
    if (!dl) return;
    dl.innerHTML = '';
    (values || []).forEach(v => {
        const opt = document.createElement('option');
        opt.value = v;
        dl.appendChild(opt);
    });
}

function syncTypeToggle(typeValue) {
    const isCatamaran = (typeValue || '').toLowerCase() === 'catamaran';
    const toggle = document.getElementById('boat-type-toggle');
    const labelMono = document.getElementById('type-label-monohull');
    const labelCat = document.getElementById('type-label-catamaran');
    const hiddenInput = document.getElementById('boat-type');

    if (toggle) {
        toggle.checked = isCatamaran;
    }
    if (labelMono && labelCat) {
        if (isCatamaran) {
            labelMono.classList.remove('active');
            labelCat.classList.add('active');
        } else {
            labelMono.classList.add('active');
            labelCat.classList.remove('active');
        }
    }
    if (hiddenInput) {
        hiddenInput.value = isCatamaran ? 'Catamaran' : 'Monohull';
    }
}

export async function showBoatForm(boatId = null) {
    previousUrl = window.location.pathname;
    dom.boatForm.reset();
    const form = dom.boatForm;

    Promise.all([getCharterCompanies(), getCruisingRegions()]).then(([cc, cr]) => {
        populateDatalist('charter-company-list', cc?.values);
        populateDatalist('cruising-region-list', cr?.values);
    }).catch(() => {});

    if (boatId) {
        dom.boatFormTitle.textContent = "Edit Boat";
        if (dom.boatAgentBtn) dom.boatAgentBtn.style.display = 'none';
        try {
            const boat = await apiFetch(`/api/v1/boat/${boatId}`);
            form.elements.id.value = boat.id;
            form.elements.name.value = boat.name || '';
            form.elements.type.value = boat.type || 'Monohull';
            syncTypeToggle(form.elements.type.value);
            form.elements.make.value = boat.make || '';
            form.elements.model.value = boat.model || '';
            form.elements.year.value = boat.year || '';
            form.elements.boatlength.value = boat.length ? (boat.length / 12) : 40;
            form.elements.beam.value = boat.beam ? (boat.beam / 12) : 15;
            form.elements.draft.value = boat.draft ? (boat.draft / 12) : 6.5;
            form.elements.mast.value = boat.mast ? (boat.mast / 12) : 50;
            form.elements.notes.value = boat.notes || '';
            form.elements.displacement.value = boat.displacement || '';
            form.elements.is_charter.checked = !!boat.is_charter;
            form.elements.charter_company.value = boat.charter_company || '';
            form.elements.cruising_region.value = boat.cruising_region || '';
            if (dom.boatAgentBtn) {
                const hasIdentity = (boat.make || boat.model) && boat.name;
                dom.boatAgentBtn.style.display = 'inline-block';
                dom.boatAgentBtn.classList.toggle('disabled', !hasIdentity);
            }
        } catch (error) {
            showError(error);
            return;
        }
    } else {
        dom.boatFormTitle.textContent = "Add New Boat";
        if (dom.boatAgentBtn) dom.boatAgentBtn.style.display = 'inline-block';
        form.elements.id.value = '';
        form.elements.type.value = 'Monohull';
        syncTypeToggle('Monohull');
        form.elements.boatlength.value = 40;
        form.elements.beam.value = 15;
        form.elements.draft.value = 6.5;
        form.elements.mast.value = 50;
    }
    dom.boatFormModal.classList.add('show');
    refreshDiagram();
}

export function validateBoatSizeField(inputElement) {
    const title = "Please use 'feet'inches' (e.g., 30'6\") or 'feet' (e.g., 30') or 'inches' (e.g., 6\") format.";
    if (!isValidFeetInchesFormat(inputElement.value)) {
        inputElement.classList.add('warning');
        inputElement.title = title;
    } else {
        inputElement.classList.remove('warning');
        inputElement.title = "";
    }
}

async function handleBoatFormSubmit(e) {
    e.preventDefault();
    const form = e.currentTarget;
    const sizeFields = ['boatlength', 'beam', 'draft', 'mast'];
    let isValid = true;

    sizeFields.forEach(fieldName => {
        const input = form.elements[fieldName];
        if (!isValidFeetInchesFormat(input.value)) {
            isValid = false;
            validateBoatSizeField(input);
        }
    });

    if (!isValid) return showError({ message: "Please correct the format of the size fields." });

    const formData = new FormData(form);
    const boatId = formData.get('id');
    const boatData = {
        id: parseInt(boatId),
        name: formData.get('name'),
        type: formData.get('type'),
        make: formData.get('make'),
        model: formData.get('model'),
        year: parseInt(formData.get('year'), 10) || null,
        length: feetInchesToInches(formData.get('boatlength')),
        beam: feetInchesToInches(formData.get('beam')),
        draft: feetInchesToInches(formData.get('draft')),
        mast: feetInchesToInches(formData.get('mast')),
        notes: formData.get('notes'),
        displacement: parseFloat(formData.get('displacement')) || 0,
        is_charter: formData.get('is_charter') === 'on',
        charter_company: formData.get('charter_company') || '',
        cruising_region: formData.get('cruising_region') || '',
    };

    const method = boatId ? 'PUT' : 'POST';
    const url = boatId ? `/api/v1/boat/${boatId}` : '/api/v1/boat';

    try {
        await apiFetch(url, {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(boatData),
        });
        dom.boatFormModal.classList.remove('show');
        history.pushState(null, '', previousUrl);
        getBoats();
    } catch (error) {
        showError(error);
    }
}

import { showLoadingText, hideLoadingText } from './loader.js';

async function handleBoatAgentSearch() {
    const form = dom.boatForm;
    const name = form.elements.name.value.trim();
    const make = form.elements.make.value.trim();
    const model = form.elements.model.value.trim();

    if (!(name && (make || model))) {
        alert('Please enter a Name and either a Make or Model to enable research.');
        return;
    }

    if (dom.boatAgentBtn.classList.contains('processing')) return;

    dom.boatAgentBtn.classList.add('disabled');
    dom.boatAgentBtn.classList.add('processing');
    showLoadingText();
    
    const boatData = {
        name: form.elements.name.value,
        make: form.elements.make.value,
        model: form.elements.model.value,
        year: parseInt(form.elements.year.value, 10) || null,
    };

    const reenable = () => {
        dom.boatAgentBtn.classList.remove('processing');
        const n = form.elements.name.value.trim();
        const m = form.elements.make.value.trim();
        const mo = form.elements.model.value.trim();
        dom.boatAgentBtn.classList.toggle('disabled', !(n && (m || mo)));
    };

    try {
        const specs = await boatAgent(boatData);
        if (specs) {
            populateBoatAgentConfirmModal(specs);
            dom.boatAgentBtn.classList.remove('processing');
        } else {
            showError({ message: "There was not enough information to find your boat's stats. Try setting a make and model." });
            reenable();
        }
    } catch (error) {
        if (error.message.includes("unmarshaling boat json")) {
            showError({ message: "There was not enough information to find your boat's stats. Try setting a make and model." });
        } else {
            showError(error);
        }
        reenable();
    } finally {
        hideLoadingText();
    }
}

function toTitleCase(str) {
    if (!str) return '';
    return str.replace(
        /\w\S*/g,
        function(txt) {
            return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
        }
    );
}

function populateBoatAgentConfirmModal(specs) {
    const specsContainer = dom.boatAgentConfirmModal.querySelector('.boat-agent-specs');
    
    const specsTable = `
        <table class="boat-specs-table">
            <tbody>
                <tr><td class="spec-label">Make</td><td class="spec-value">${specs.make}</td></tr>
                <tr><td class="spec-label">Model</td><td class="spec-value">${specs.model}</td></tr>
                <tr><td class="spec-label">Year</td><td class="spec-value">${specs.year}</td></tr>
                <tr><td class="spec-label">Type</td><td class="spec-value">${toTitleCase(specs.type)}</td></tr>
                <tr><td class="spec-label">Length</td><td class="spec-value">${inchesToFeetInches(specs.length)}</td></tr>
                <tr><td class="spec-label">Beam</td><td class="spec-value">${inchesToFeetInches(specs.beam)}</td></tr>
                <tr><td class="spec-label">Draft</td><td class="spec-value">${inchesToFeetInches(specs.draft)}</td></tr>
                <tr><td class="spec-label">Mast</td><td class="spec-value">${inchesToFeetInches(specs.mast)}</td></tr>
                <tr><td class="spec-label">Displacement</td><td class="spec-value">${specs.displacement ? `${specs.displacement.toLocaleString()} lbs` : '—'}</td></tr>
            </tbody>
        </table>
    `;

    let sourcesList = '';
    if (specs.source && specs.source.length > 0) {
        sourcesList = `
            <div class="sources-container">
                <h4 class="sources-title">Sources: from Google</h4>
                <ul class="sources-list">
                    ${specs.source.map(source => `<li><a href="${source}" target="_blank" rel="noopener noreferrer">${source}</a></li>`).join('')}
                </ul>
            </div>
        `;
    }

    specsContainer.innerHTML = specsTable + sourcesList;
    dom.boatAgentConfirmModal.dataset.specs = JSON.stringify(specs);
    dom.boatAgentConfirmModal.classList.add('show');
}

function handleBoatAgentConfirm() {
    const specs = JSON.parse(dom.boatAgentConfirmModal.dataset.specs);
    const form = dom.boatForm;

    form.elements.make.value = specs.make || '';
    form.elements.model.value = specs.model || '';
    form.elements.year.value = specs.year || '';

    const typeSelect = form.elements.type;
    if (specs.type) {
        const specsTypeLower = specs.type.toLowerCase();
        if (specsTypeLower.includes('catamaran') || specsTypeLower.includes('cat')) {
            typeSelect.value = 'Catamaran';
        } else {
            typeSelect.value = 'Monohull';
        }
        syncTypeToggle(typeSelect.value);
    } else {
        typeSelect.value = 'Monohull';
        syncTypeToggle('Monohull');
    }

    form.elements.boatlength.value = specs.length ? (specs.length / 12) : 40;
    form.elements.beam.value = specs.beam ? (specs.beam / 12) : 15;
    form.elements.draft.value = specs.draft ? (specs.draft / 12) : 6.5;
    form.elements.mast.value = specs.mast ? (specs.mast / 12) : 50;

    if (specs.displacement) {
        form.elements.displacement.value = specs.displacement;
    }

    dom.boatAgentConfirmModal.classList.remove('show');
    dom.boatAgentBtn.classList.add('disabled');
    refreshDiagram();
}

async function handleBoatAssociationChange(e) {
    const select = e.currentTarget;
    const trackId = select.dataset.trackId;
    const newBoatId = select.value;

    const oldBoatId = trackDataCache[trackId]?.boat?.id;
    if (newBoatId == oldBoatId) return;

    try {
        const track = trackDataCache[trackId];
        if (track.boat) {
            await apiFetch(`/api/v1/boat/${track.boat.id}/track/${trackId}`, { method: 'DELETE' });
            track.boat = null;
        }

        if (newBoatId) {
            const boat = boatDataCache[newBoatId];
            await apiFetch(`/api/v1/boat/${newBoatId}/track/${trackId}`, { method: 'POST' });
            track.boat = boat;
        }
        updateLegend();
    } catch (error) {
        showError(error);
        select.value = oldBoatId || '';
    }
}

function refreshUI() {
    updateLegend();
    updateMapBounds();
    updateMarkersVisibility();
}

export function clearAllSelections(e) {
    if (e) {
        e.preventDefault();
        history.pushState(null, '', '/');
    }
    setActiveTrip(null);
    clearAllMapLayers();
    clearTrackState();
    document.querySelectorAll('.track-toggle:checked').forEach(cb => {
        cb.checked = false;
        cb.closest('li').classList.remove('current');
    });
    updateMarkersVisibility();
    updateLegend();
    resetColorIndex();
    document.querySelectorAll('.trips-list .trip-list-item.current').forEach(item => item.classList.remove('current'));
}

function handleTrackColorChange(e) {
    const picker = e.currentTarget;
    const trackId = picker.dataset.trackId;
    const newColor = picker.value;
    if (trackDataCache[trackId]) trackDataCache[trackId].color = newColor;
    if (mapInstance.getLayer(`line-layer-${trackId}`)) mapInstance.setPaintProperty(`line-layer-${trackId}`, 'line-color', newColor);
}

export function updateLegend() {
    const count = visibleTrackIds ? visibleTrackIds.size : 0;
    const statsContainer = document.getElementById('stats-container');
    const showLegendBtn = document.getElementById('show-legend-mobile-btn');
    const showStatsBtn = document.getElementById('show-stats-mobile-btn');
    const isMobile = window.innerWidth <= 768;

    // Reset states
    if (dom.header) {
        dom.header.classList.remove('mobile-fullscreen', 'mobile-hidden');
    }
    if (showLegendBtn) showLegendBtn.classList.remove('visible');
    if (showStatsBtn) showStatsBtn.classList.remove('visible');

    dom.tracksPanel.classList.toggle('centered', count === 0);
    if (dom.toggleTracksButton) dom.toggleTracksButton.classList.toggle('hidden', count === 0);

    const clearAllButton = document.querySelector('.track-action-btn.clear-all');
    if (clearAllButton) clearAllButton.style.display = count > 0 ? 'flex' : 'none';

    dom.mapContainer.classList.toggle('hidden', count === 0);

    if (count === 0) {
        if (dom.singleTrackView) dom.singleTrackView.style.display = 'none';
        if (dom.multiTrackView) dom.multiTrackView.style.display = 'none';
        if (statsContainer) statsContainer.classList.remove('hidden');
        renderLegendEmptyView();
        if (dom.header) dom.header.classList.add('hidden');
        if (isMobile && showStatsBtn) showStatsBtn.classList.add('visible');
        return;
    }

    if (statsContainer) {
        statsContainer.classList.add('hidden');
        statsContainer.classList.remove('visible');
    }

    if (dom.header) dom.header.classList.remove("hidden");

    if (count === 1) {
        if (isMobile) {
            if (showLegendBtn) {
                showLegendBtn.classList.add('visible');
                showLegendBtn.textContent = 'Show Details';
            }
            if (dom.header) dom.header.classList.add('mobile-hidden');
        } 
        renderLegendSingleTrackView();
    } else { // count > 1
        if (isMobile) {
            if (showLegendBtn) {
                showLegendBtn.classList.add('visible');
                showLegendBtn.textContent = 'Show Details';
            }
            if (dom.header) dom.header.classList.add('mobile-hidden');
        }
        renderLegendMultiTrackView();
        if (dom.editTrackButton) dom.editTrackButton.style.display = 'none';
    }
}

function renderLegendEmptyView() {
    if (dom.header) dom.header.classList.add("hidden");
    if (dom.tracksPanel) {
        dom.tracksPanel.classList.remove('hidden', 'collapsed');
    }
    if (dom.toggleTracksButton) {
        dom.toggleTracksButton.querySelector('.material-symbols-outlined').textContent = 'menu_open';
        dom.toggleTracksButton.title = 'Collapse track list';
    }
    const showLegendBtn = document.getElementById('show-legend-mobile-btn');
    if (showLegendBtn) showLegendBtn.classList.remove('visible');
}

function renderLegendSingleTrackView() {
    if (dom.singleTrackView) dom.singleTrackView.style.display = 'block';
    if (dom.multiTrackView) dom.multiTrackView.style.display = 'none';

    const [trackId] = visibleTrackIds;
    const track = trackDataCache[trackId];
    if (!track) return;

    const timeZone = (track.weather_report && track.weather_report.time_zone && !['utc', 'gmt'].includes(track.weather_report.time_zone.toLowerCase())) ? track.weather_report.time_zone : 'UTC';
    const start = DateTime.fromISO(track.start).setZone(timeZone);
    const end = DateTime.fromISO(track.end).setZone(timeZone);

    // Calculate duration from local time (wall clock difference)
    const duration = end.diff(start);

    if (dom.shareTrackButton) dom.shareTrackButton.dataset.trackId = track.id;
    if (dom.addPhotoBtn) dom.addPhotoBtn.dataset.trackId = track.id;
    if (dom.legendTitle) {
        dom.legendTitle.textContent = track.name;
        dom.legendTitle.dataset.trackId = track.id;
    }
    if (dom.legendDate) dom.legendDate.innerHTML = formatTrackDateRange(track.start, track.end, timeZone);
    if (dom.legendDistance) dom.legendDistance.innerHTML = `${track.distance.toFixed(2)} nm`;
    if (dom.legendDuration) dom.legendDuration.innerHTML = duration.toFormat("h'h' m'm'");
    if (dom.legendJourneyTimes) dom.legendJourneyTimes.innerHTML = `${start.toLocaleString(DateTime.TIME_SIMPLE)} - ${end.toLocaleString(DateTime.TIME_SIMPLE)} ${end.toFormat('ZZZZ')}`;
    if (dom.legendTopSpeed) dom.legendTopSpeed.innerHTML = `${track.speed_top.toFixed(1)} kts`;
    if (dom.legendAverageSpeed) dom.legendAverageSpeed.innerHTML = `${track.speed_avg.toFixed(1)} kts`;

    if (track.weather_report) {
        const { description, temperature, wind } = track.weather_report;
        if (dom.legendWeatherDescription && description) {
            dom.legendWeatherDescription.innerHTML = `<span class="text">${description}</span><span class="icon-holder"><i class="material-symbols-outlined">${getIconForWeather(description)}</i></span>`;
        }
        if (dom.legendWeatherTemperature && temperature) {
            dom.legendWeatherTemperature.innerHTML = `<span class="text">Low: <strong>${parseInt(temperature.low)}°</strong> / High: <strong>${parseInt(temperature.high)}°</strong></span><span class="icon-holder"><i class="material-symbols-outlined">thermostat</i></span>`;
        }
        if (dom.legendWeatherWindspeed && wind) {
            dom.legendWeatherWindspeed.innerHTML = `<span class="text"><strong>${parseInt(wind.low)}-${parseInt(wind.high)} ${wind.unit}</strong>, Gusts <strong>${parseInt(wind.gust)} ${wind.unit}</strong></span><span class="icon-holder"><i class="material-symbols-outlined">air</i></span>`;
        }
        if (dom.legendWeatherWindDirection && wind) {
            const rotation = (parseInt(wind.direction) + 180) % 360;
            dom.legendWeatherWindDirection.innerHTML = `<span class="text">${degrees(wind.direction)} (${wind.direction}°)</span><span class="icon-holder"><i style="transform: rotate(${rotation}deg);" class="material-symbols-outlined" id="wind-direction-icon">navigation</i></span>`;
        }
    }

    const boatSelector = document.getElementById('boat-selector');
    if (boatSelector) {
        boatSelector.innerHTML = '';

        const defaultOption = document.createElement('option');
        defaultOption.value = '';
        defaultOption.textContent = '-- assign a boat --';
        boatSelector.appendChild(defaultOption);

        for (const boatId in boatDataCache) {
            const boat = boatDataCache[boatId];
            const option = document.createElement('option');
            option.value = boat.id;
            option.textContent = boat.name;
            boatSelector.appendChild(option);
        }

        if (track.boat) {
            boatSelector.value = track.boat.id;
        } else {
            boatSelector.value = '';
        }
        
        boatSelector.removeEventListener('change', handleBoatAssociationChange);
        boatSelector.addEventListener('change', handleBoatAssociationChange);
        boatSelector.dataset.trackId = track.id;

        if (currentUser && currentUser.id === track.owner_id) {
             boatSelector.disabled = false;
        } else {
             boatSelector.disabled = true;
        }
    }

    if (dom.legendDescriptionContainer) dom.legendDescriptionContainer.classList.remove("hidden");
    if (dom.legendDescriptionText) dom.legendDescriptionText.innerHTML = (track.description || "").replace(/\n/g, "<br>");

    const isSharedView = dom.authControls.classList.contains('hidden');
    const isOwner = currentUser && currentUser.id === track.owner_id;
    
    if (dom.legendActions) {
        dom.legendActions.innerHTML = '';
        dom.legendActions.style.display = isSharedView ? 'none' : 'contents';

        if (!isSharedView) {
            if (dom.exportBtn) {
                dom.exportBtn.classList.add('tb-btn');
                dom.legendActions.appendChild(dom.exportBtn);
            }

            if (isOwner) {
                const exportZipBtn = document.createElement('button');
                exportZipBtn.className = 'tb-btn';
                exportZipBtn.title = 'Export track data as ZIP';
                exportZipBtn.innerHTML = `<span class="material-symbols-outlined">folder_zip</span>`;
                exportZipBtn.addEventListener('click', () => {
                    window.location.href = `/api/v1/track/${track.id}/export`;
                });
                dom.legendActions.appendChild(exportZipBtn);

                if (dom.shareTrackButton) {
                    dom.shareTrackButton.classList.add('tb-btn');
                    dom.legendActions.appendChild(dom.shareTrackButton);
                }

                if (dom.editTrackButton) {
                    dom.editTrackButton.classList.add('tb-btn');
                    dom.legendActions.appendChild(dom.editTrackButton);
                }

                if (dom.addPhotoBtn) {
                    dom.addPhotoBtn.classList.add('tb-btn');
                    dom.addPhotoBtn.style.display = 'flex';
                    dom.legendActions.appendChild(dom.addPhotoBtn);
                }
            }

            const manageCrewBtn = document.createElement('button');
            manageCrewBtn.className = 'tb-btn';
            manageCrewBtn.title = isOwner ? 'Manage Crew' : 'View Crew';
            manageCrewBtn.innerHTML = `<span class="material-symbols-outlined">groups</span>`;
            manageCrewBtn.addEventListener('click', () => openCrewModal(track.id));
            dom.legendActions.appendChild(manageCrewBtn);

            const divider = document.createElement('div');
            divider.className = 'tb-divider';
            dom.legendActions.appendChild(divider);
        }
    }

    loadAndRenderTrackCrewHeader(track.id);
}

function renderLegendMultiTrackView() {
    dom.singleTrackView.style.display = 'none';
    dom.multiTrackView.style.display = 'block';

    const visibleTracks = [...visibleTrackIds].map(id => trackDataCache[id]).filter(Boolean);
    const totalDistance = visibleTracks.reduce((total, track) => total + track.distance, 0);
    visibleTracks.sort((a, b) => a.start.localeCompare(b.start));

    // Calculate total duration using localtime
    const totalDurationMs = visibleTracks.reduce((total, track) => {
        const timeZone = (track.weather_report && track.weather_report.time_zone && !['utc', 'gmt'].includes(track.weather_report.time_zone.toLowerCase())) ? track.weather_report.time_zone : 'UTC';
        const start = DateTime.fromISO(track.start).setZone(timeZone);
        const end = DateTime.fromISO(track.end).setZone(timeZone);
        return total + end.diff(start).toMillis();
    }, 0);

    const formatDurationMs = (ms) => {
        const totalMinutes = Math.floor(ms / (1000 * 60));
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours}h ${minutes}m`;
    };

    const isSharedView = dom.authControls.classList.contains('hidden');
    const isOwner = activeTrip && currentUser && currentUser.id === activeTrip.owner_id;

    const shareBtnHtml = activeTrip
        ? `<button class="tb-btn share-trip-btn" title="Share this trip" data-trip-id="${activeTrip.id}"><span class="material-symbols-outlined">share</span></button>`
        : '';
    const exportZipBtnHtml = isOwner
        ? `<button class="tb-btn export-trip-zip-btn" title="Export trip folder (zip)"><span class="material-symbols-outlined">folder_zip</span></button>`
        : '';

    const toolbarHtml = `<div class="legend-toolbar">
        <button class="tb-btn export-trip-png-btn" title="Export as PNG"><span class="material-symbols-outlined">download</span></button>
        ${exportZipBtnHtml}
        ${shareBtnHtml}
        <div class="tb-divider"></div>
        <button class="tb-btn toggle-markers-btn" title="Toggle markers"><span class="material-symbols-outlined">${photosVisible ? 'visibility' : 'visibility_off'}</span></button>
        <button class="tb-btn compact-toggle-btn" title="Toggle compact view"><span class="material-symbols-outlined">height</span></button>
        <div class="tb-divider"></div>
        <button class="tb-btn close close-multi-track-legend-btn" title="Close"><span class="material-symbols-outlined">close</span></button>
    </div>`;

    const tripDetailsHtml = `<div class="title-container">
        <div class="trip-details">
            <h3 class="trip-title" contenteditable="false" title="Trip Name"></h3>
            <p class="trip-description" contenteditable="false" title="Trip Description" data-placeholder="Add a description for this trip..."></p>
            <div class="trip-crew-summary"></div>
        </div>
    </div>`;

    const trackListHtml = `<div class="multi-track-legend">
        <div class="collapsible-content">
            <table class="trip-table">
                <colgroup>
                    <col style="width: 20%">
                    <col style="width: 11%">
                    <col style="width: 12%">
                    <col style="width: 17%">
                    <col style="width: 9%">
                    <col style="width: 9%">
                    <col style="width: 10%">
                    <col style="width: 12%">
                </colgroup>
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Date</th>
                        <th>Weather</th>
                        <th>Wind</th>
                        <th class="num">Avg</th>
                        <th class="num">Top</th>
                        <th class="num">Duration</th>
                        <th class="num">Distance</th>
                    </tr>
                </thead>
                <tbody>${visibleTracks.map(track => {
        const { weather_report, boat, description, name, start, end, distance, speed_avg, speed_top, id, color } = track;
        const temperature = weather_report ? weather_report.temperature : { low: 0, high: 0, unit: 'F' };
        const wind = (weather_report && weather_report.wind) ? weather_report.wind : { low: 0, high: 0, unit: 'kts', direction: 0 };
        const weatherIcon = weather_report ? getIconForWeather(weather_report.description) : 'cloud';
        const windDir = degrees(wind.direction);
        const windSpeed = `${parseInt(wind.low)}-${parseInt(wind.high)} ${wind.unit}`;
        const rotation = (parseInt(wind.direction) + 180) % 360;
        const trackColor = color || '#800000';

        const timeZone = (weather_report && weather_report.time_zone && !['utc', 'gmt'].includes(weather_report.time_zone.toLowerCase())) ? weather_report.time_zone : 'UTC';
        const tStart = DateTime.fromISO(start).setZone(timeZone);
        const tEnd = DateTime.fromISO(end).setZone(timeZone);
        const trackDuration = tEnd.diff(tStart).toFormat("h'h' m'm'");

        const trackNameHtml = isSharedView ? name : `<a href="#" class="track-link" data-track-id="${id}">${name}</a>`;

        return `<tr class="track-data-row" data-track-id="${id}">
            <td class="name-cell" data-label="Name">
                <input type="color" class="legend-track-color-picker" data-track-id="${id}" value="${trackColor}" title="Change track color">
                ${trackNameHtml}
            </td>
            <td data-label="Date">${formatTrackDateRange(start, end, timeZone)}</td>
            <td data-label="Weather">
                <div class="weather-cell">
                    <span class="material-symbols-outlined">${weatherIcon}</span>
                    <span>${parseInt(temperature.low)}-${parseInt(temperature.high)} ${temperature.unit}</span>
                </div>
            </td>
            <td data-label="Wind">
                <span class="material-symbols-outlined" style="transform: rotate(${rotation}deg);">navigation</span>
                <span>${windDir} ${windSpeed}</span>
            </td>
            <td class="num" data-label="Avg">${speed_avg.toFixed(1)} kts</td>
            <td class="num" data-label="Top">${speed_top.toFixed(1)} kts</td>
            <td class="num" data-label="Duration">${trackDuration}</td>
            <td class="num dist" data-label="Distance">${distance.toFixed(2)} nm</td>
        </tr>
        <tr class="note-row">
            <td colspan="8">${description || ''}</td>
        </tr>`;
    }).join('')}</tbody>
                <tfoot>
                    <tr>
                        <td class="label" colspan="6">Totals</td>
                        <td class="total-val">${formatDurationMs(totalDurationMs)}</td>
                        <td class="total-val">${totalDistance.toFixed(2)} nm</td>
                    </tr>
                </tfoot>
            </table>
        </div>
    </div>`;

    const tripActionsHtml = `<div class="trip-actions"><button class="create-trip-btn button-base">Save Trip</button></div>`;
    dom.multiTrackView.innerHTML = toolbarHtml + tripDetailsHtml + trackListHtml + tripActionsHtml;

    const closeBtn = dom.multiTrackView.querySelector('.close-multi-track-legend-btn');
    if (closeBtn) closeBtn.addEventListener('click', toggleLegend);

    const exportPngBtn = dom.multiTrackView.querySelector('.export-trip-png-btn');
    if (exportPngBtn) exportPngBtn.addEventListener('click', captureAndExport);

    const exportZipBtn = dom.multiTrackView.querySelector('.export-trip-zip-btn');
    if (exportZipBtn) exportZipBtn.addEventListener('click', () => {
        window.location.href = `/api/v1/trip/${activeTrip.id}/export`;
    });

    const shareTripBtnEl = dom.multiTrackView.querySelector('.share-trip-btn');
    if (shareTripBtnEl) shareTripBtnEl.addEventListener('click', handleShareTripClick);

    const multiToggleMarkersBtn = dom.multiTrackView.querySelector('.toggle-markers-btn');
    if (multiToggleMarkersBtn) multiToggleMarkersBtn.addEventListener('click', handleToggleMarkers);

    const compactToggleButton = dom.multiTrackView.querySelector('.compact-toggle-btn');
    if (compactToggleButton) {
        compactToggleButton.addEventListener('click', () => {
            dom.header.classList.toggle('compact');
        });
    }

    const tripTitleEl = dom.multiTrackView.querySelector('.trip-title');
    const tripDescriptionEl = dom.multiTrackView.querySelector('.trip-description');
    const createTripBtn = dom.multiTrackView.querySelector('.create-trip-btn');

    if (activeTrip) {
        tripTitleEl.textContent = activeTrip.name;
        tripDescriptionEl.innerText = activeTrip.description || '';
        tripTitleEl.contentEditable = true;
        tripDescriptionEl.contentEditable = true;
        tripTitleEl.addEventListener('blur', updateTripDetails);
        tripDescriptionEl.addEventListener('blur', updateTripDetails);
        if (createTripBtn) createTripBtn.style.display = 'none';

        loadAndRenderTripCrewHeader(activeTrip.id);
    } else {
        tripTitleEl.textContent = 'Combined Voyage';
        tripTitleEl.contentEditable = true;
        tripDescriptionEl.contentEditable = true;
        if (createTripBtn) createTripBtn.style.display = 'block';
    }

    if (!isSharedView) {
        document.querySelectorAll('.boat-edit-btn').forEach(button => {
            button.addEventListener('click', (e) => {
                const boatId = e.currentTarget.dataset.boatId;
                showBoatForm(boatId);
            });
        });

        document.querySelectorAll('.track-link').forEach(link => {
            link.addEventListener('click', e => {
                e.preventDefault();
                showSingleTrackFromMultiView(e.currentTarget.dataset.trackId);
            });
        });
    }

    document.querySelectorAll('.legend-track-color-picker').forEach(picker => picker.addEventListener('input', handleTrackColorChange));
    if (createTripBtn) createTripBtn.addEventListener('click', createTrip);



}


function showSingleTrackFromMultiView(trackId) {
    clearAllSelections();
    const trackToggle = document.getElementById(`track-toggle-${trackId}`);
    if (trackToggle) {
        trackToggle.checked = true;
        trackToggle.dispatchEvent(new Event('change', { bubbles: true }));
    }
}

export function createActionButtons() {
    const uploadTrackBtn = document.createElement("a");
    uploadTrackBtn.className = 'track-action-btn add';
    uploadTrackBtn.innerHTML = `<span class="label">Upload new track</span><span class="material-symbols-outlined">arrow_circle_up</span>`;
    uploadTrackBtn.addEventListener("click", addTrack);
    dom.uploadTrackBtn = uploadTrackBtn;

    const addBoatBtn = document.createElement('a');
    addBoatBtn.className = 'track-action-btn add';
    addBoatBtn.innerHTML = `<span class="label">Add New Boat</span><span class="material-symbols-outlined">add_circle</span>`;
    addBoatBtn.addEventListener('click', () => showBoatForm());
    dom.addBoatBtn = addBoatBtn;

    const clearAllBtn = document.createElement("a");
    clearAllBtn.className = `track-action-btn clear-all`;
    clearAllBtn.innerHTML = `<span class="label">Clear all selections</span><span class="material-symbols-outlined">layers_clear</span>`;
    clearAllBtn.addEventListener("click", e => {
        clearAllSelections(e);
        updateMapBounds();
    });

    const bulkPhotoUploadBtn = document.createElement("a");
    bulkPhotoUploadBtn.className = 'track-action-btn add';
    bulkPhotoUploadBtn.innerHTML = `<span class="label">Upload photos</span><span class="material-symbols-outlined">add_a_photo</span>`;
    bulkPhotoUploadBtn.addEventListener("click", () => dom.bulkPhotoInput.click());
    dom.bulkPhotoUploadBtn = bulkPhotoUploadBtn;

    const bulkPhotoInput = document.createElement("input");
    bulkPhotoInput.type = 'file';
    bulkPhotoInput.multiple = true;
    bulkPhotoInput.accept = 'image/jpeg,image/heic,image/heif,.heic,.heif';
    bulkPhotoInput.style.display = 'none';
    bulkPhotoInput.addEventListener('change', handlePhotoUpload);
    document.body.appendChild(bulkPhotoInput);
    dom.bulkPhotoInput = bulkPhotoInput;

    dom.dynamicActions.appendChild(uploadTrackBtn);
    dom.dynamicActions.appendChild(bulkPhotoUploadBtn);
    dom.dynamicActions.appendChild(addBoatBtn);
    dom.dynamicActions.appendChild(clearAllBtn);
}

export function updateActionButtons(activeTab) {
    const isTrackTab = activeTab === 'tracks' || activeTab === 'trips';
    const isBoatTab = activeTab === 'boats';
    if (dom.uploadTrackBtn) dom.uploadTrackBtn.style.display = isTrackTab ? 'flex' : 'none';
    if (dom.bulkPhotoUploadBtn) dom.bulkPhotoUploadBtn.style.display = isTrackTab ? 'flex' : 'none';
    if (dom.addBoatBtn) dom.addBoatBtn.style.display = isBoatTab ? 'flex' : 'none';
}

export function showError(error) {
    if (!dom.errorDisplay) return;
    dom.errorDisplay.classList.add("show");
    let msg = "An unexpected error occurred.";

    if (error.message) {
        msg = error.message;
    } else if (typeof error === 'string') {
        msg = error;
    }

    if (!msg.toLowerCase().startsWith("error:")) {
        msg = "Error: " + msg;
    }
    
    dom.errorMessage.textContent = msg;
    console.error("Frontend Error:", error);
}

export function hideError() {
    dom.errorDisplay.classList.remove("show");
}

function promptForDelete(e) {
    e.preventDefault();
    e.stopPropagation();
    const btn = e.currentTarget;
    const isTrip = btn.classList.contains('delete-trip-btn');
    const isBoat = btn.classList.contains('delete-boat-btn');
    let type, id, name;

    if (isTrip) {
        type = 'trip';
        id = btn.dataset.tripId;
        name = btn.dataset.tripName;
    } else if (isBoat) {
        type = 'boat';
        id = btn.dataset.boatId;
        name = btn.dataset.boatName;
    } else {
        type = 'track';
        id = btn.dataset.trackId;
        name = btn.dataset.trackName;
    }

    dom.deleteConfirmMessage.innerHTML = `Are you sure you want to delete the ${type}:<br /> <i>${name}</i>? <br /><br />This action cannot be undone.`;
    dom.deleteConfirmButton.dataset.deleteId = id;
    dom.deleteConfirmButton.dataset.deleteType = type;
    dom.deleteConfirmModal.classList.add("show");
}

export function executeDelete(e) {
    const btn = e.currentTarget;
    const id = btn.dataset.deleteId;
    const type = btn.dataset.deleteType;

    if (!id || !type) return showError({ message: "Could not determine what to delete." });

    let url;
    switch (type) {
        case 'trip': url = `/api/v1/trip/${id}`; break;
        case 'boat': url = `/api/v1/boat/${id}`; break;
        default: url = `${apiUrl}/${id}`; break;
    }

    apiFetch(url, { method: "DELETE" }).then(() => {
        hideDeleteConfirm();
        switch (type) {
            case 'trip':
                if (activeTrip && activeTrip.id.toString() === id) clearAllSelections();
                getTrips();
                break;
            case 'boat':
                getBoats();
                break;
            default:
                if (visibleTrackIds.has(id)) {
                    visibleTrackIds.delete(id);
                    delete trackDataCache[id];
                    removeTrackFromMap(id);
                    refreshUI();
                }
                getTracks();
                break;
        }
    }).catch(showError);
}

export function hideDeleteConfirm() {
    dom.deleteConfirmModal.classList.remove("show");
}

export function toggleLegend(e) {
    const legend = document.querySelector('.legend');
    const showLegendBtn = document.getElementById('show-legend-mobile-btn');

    if (window.innerWidth <= 768) {
        legend.classList.remove('mobile-fullscreen');
        legend.classList.add('mobile-hidden');
        if (showLegendBtn) {
            showLegendBtn.classList.add('visible');
            showLegendBtn.textContent = 'Show Details';
        }
    } else {
        const isHidden = legend.classList.toggle('hidden');
        const btn = e.currentTarget;
        btn.title = isHidden ? 'Show legend' : 'Hide legend';
        btn.querySelector('.material-symbols-outlined').textContent = isHidden ? 'visibility' : 'close';
    }
}

export function toggleTracksPanel() {
    const isCollapsed = dom.tracksPanel.classList.toggle('collapsed');
    const icon = dom.toggleTracksButton.querySelector('.material-symbols-outlined');
    icon.textContent = isCollapsed ? 'menu' : 'menu_open';
    dom.toggleTracksButton.title = isCollapsed ? 'Expand track list' : 'Collapse track list';
}

export function handleShareClick(e) {
    const trackId = e.currentTarget.dataset.trackId;
    if (!trackId) return;

    apiFetch(`${apiUrl}/${trackId}/share`, { method: 'POST' }).then(share => {
        const shareUrl = `${window.location.origin}/track/share/${share.share_id}`;
        dom.shareModal.querySelector('.modal-title').textContent = 'Share Track';
        dom.shareModal.querySelector('p').textContent = 'Anyone with this link can view the track.';
        dom.shareUrlInput.value = shareUrl;
        dom.shareModal.classList.add('show');
    }).catch(showError);
}

export function handleShareTripClick(e) {
    const tripId = e.currentTarget.dataset.tripId;
    if (!tripId) return;

    apiFetch(`/api/v1/trip/${tripId}/share`, { method: 'POST' }).then(share => {
        const shareUrl = `${window.location.origin}/trip/share/${share.share_id}`;
        dom.shareModal.querySelector('.modal-title').textContent = 'Share Trip';
        dom.shareModal.querySelector('p').textContent = 'Anyone with this link can view the trip.';
        dom.shareUrlInput.value = shareUrl;
        dom.shareModal.classList.add('show');
    }).catch(showError);
}

function handleEditPersonNameClick() {
    dom.personNameViewMode.classList.add('hidden');
    dom.personNameEditMode.classList.remove('hidden');
    dom.personNameInput.value = dom.personName.textContent;
    dom.personNameInput.focus();
}

function handleCancelPersonNameClick() {
    dom.personNameEditMode.classList.add('hidden');
    dom.personNameViewMode.classList.remove('hidden');
}

function handleSavePersonNameClick() {
    const newName = dom.personNameInput.value.trim();
    if (!newName) return showError({ message: "Name cannot be empty." });

    dom.savePersonNameBtn.disabled = true;
    dom.savePersonNameBtn.textContent = "Saving...";

    updatePersonProfile({ name: newName }).then(updatedPerson => {
        dom.personName.textContent = updatedPerson.name;
        setCurrentUser(updatedPerson);
        if (dom.userAvatar) {
            dom.userAvatar.alt = `${updatedPerson.name}'s avatar`;
        }
        handleCancelPersonNameClick();
    }).catch(showError).finally(() => {
        dom.savePersonNameBtn.disabled = false;
        dom.savePersonNameBtn.textContent = "Save";
    });
}

async function openCredentialModal() {
    if (!dom.credentialModal) return;
    dom.credentialModal.classList.add('show');
    hideCredentialForm();
    await refreshCredentialLists();
}

async function refreshCredentialLists() {
    try {
        const [credentials, courses] = await Promise.all([
            listCredentials('credential'),
            listCredentials('course'),
        ]);
        renderCredentialList(dom.credentialList, credentials || []);
        renderCredentialList(dom.courseList, courses || []);
    } catch (error) {
        showError(error);
    }
}

function renderCredentialList(listEl, items) {
    if (!listEl) return;
    listEl.innerHTML = '';
    if (!items.length) {
        listEl.innerHTML = '<li class="cred-empty">None added yet.</li>';
        return;
    }
    items.forEach(item => {
        const li = document.createElement('li');
        li.className = 'cred-item';
        const meta = [];
        if (item.credential_number) meta.push(item.credential_number);
        if (item.issue_date) meta.push('Issued: ' + item.issue_date.slice(0, 10));
        if (item.expiration_date) meta.push('Expires: ' + item.expiration_date.slice(0, 10));
        li.innerHTML = `
            <div class="cred-item-info">
                <span class="cred-item-title">${escapeHtml(item.name)}</span>
                <span class="cred-item-org">${escapeHtml(item.organization)}</span>
                ${meta.length ? `<span class="cred-item-meta">${meta.map(escapeHtml).join(' &bull; ')}</span>` : ''}
            </div>
            <div class="cred-item-actions">
                <button type="button" class="button-base small edit-cred-btn" data-id="${item.id}" data-kind="${item.kind}">Edit</button>
                <button type="button" class="button-base small delete-cred-btn" data-id="${item.id}" data-kind="${item.kind}">Delete</button>
            </div>`;
        li.querySelector('.edit-cred-btn').addEventListener('click', () => showCredentialForm(item.kind, item));
        li.querySelector('.delete-cred-btn').addEventListener('click', () => handleDeleteCredential(item.id));
        listEl.appendChild(li);
    });
}

async function showCredentialForm(kind, cred = null) {
    if (!dom.credFormContainer) return;
    dom.credFormTitle.textContent = cred ? `Edit ${kind === 'credential' ? 'Credential' : 'Course'}` : `Add ${kind === 'credential' ? 'Credential' : 'Course'}`;
    dom.credIdInput.value = cred ? cred.id : '';
    dom.credKindInput.value = kind;
    dom.credOrgInput.value = cred ? cred.organization : '';
    dom.credNameInput.value = cred ? cred.name : '';
    dom.credNumberInput.value = cred ? (cred.credential_number || '') : '';
    dom.credIssueDateInput.value = cred && cred.issue_date ? cred.issue_date.slice(0, 10) : '';
    dom.credExpiryDateInput.value = cred && cred.expiration_date ? cred.expiration_date.slice(0, 10) : '';
    dom.credNotesInput.value = cred ? (cred.notes || '') : '';
    dom.credFormContainer.classList.remove('hidden');

    try {
        const [orgs, names] = await Promise.all([
            getCredentialOrganizations(kind),
            getCredentialNames(kind, cred ? cred.organization : ''),
        ]);
        populateDatalist('cred-org-list', (orgs && orgs.values) || []);
        populateDatalist('cred-name-list', (names && names.values) || []);
    } catch (_) {}

    dom.credOrgInput.addEventListener('change', async () => {
        try {
            const names = await getCredentialNames(dom.credKindInput.value, dom.credOrgInput.value);
            populateDatalist('cred-name-list', (names && names.values) || []);
        } catch (_) {}
    }, { once: true });

    dom.credFormContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

function hideCredentialForm() {
    if (dom.credFormContainer) dom.credFormContainer.classList.add('hidden');
    if (dom.credForm) dom.credForm.reset();
}

async function handleCredentialFormSubmit(e) {
    e.preventDefault();
    const id = dom.credIdInput.value ? parseInt(dom.credIdInput.value, 10) : 0;
    const kind = dom.credKindInput.value;
    const payload = {
        kind,
        organization: dom.credOrgInput.value.trim(),
        name: dom.credNameInput.value.trim(),
        credential_number: dom.credNumberInput.value.trim() || null,
        issue_date: dom.credIssueDateInput.value ? dom.credIssueDateInput.value + 'T00:00:00Z' : null,
        expiration_date: dom.credExpiryDateInput.value ? dom.credExpiryDateInput.value + 'T00:00:00Z' : null,
        notes: dom.credNotesInput.value.trim() || null,
    };
    try {
        if (id) {
            await updateCredential(id, payload);
        } else {
            await createCredential(payload);
        }
        hideCredentialForm();
        await refreshCredentialLists();
        const activeTab = kind === 'credential' ? 'credentials-tab' : 'courses-tab';
        dom.credTabs.forEach(t => t.classList.toggle('active', t.dataset.tab === activeTab));
        dom.credTabContents.forEach(c => c.classList.toggle('active', c.id === activeTab));
    } catch (error) {
        showError(error);
    }
}

async function handleDeleteCredential(id) {
    try {
        await deleteCredentialById(id);
        await refreshCredentialLists();
    } catch (error) {
        showError(error);
    }
}

async function showPersonModal() {
    try {
        const person = await apiFetch('/api/v1/me');
        if (person) {
            dom.personName.textContent = person.name;
            if (dom.personNameEditMode) dom.personNameEditMode.classList.add('hidden');
            if (dom.personNameViewMode) dom.personNameViewMode.classList.remove('hidden');

            dom.personEmail.textContent = person.email;
            dom.personPicture.src = person.picture_url;
            dom.apiKeyDisplay.textContent = '';
            dom.apiKeyDisplay.classList.add('hidden');
            dom.getApiKeyBtn.classList.remove('hidden');
            dom.copyApiKeyBtn.classList.add('hidden');
            dom.rotateApiKeyBtn.classList.add('hidden');
            dom.personModal.classList.add('show');
        }
    } catch (error) {
        showError(error);
    }
}

async function handleGetApiKey() {
    try {
        const key = await apiFetch('/api/v1/me/apikey');
        if (key && key.api_key) {
            dom.apiKeyDisplay.textContent = key.api_key;
            dom.apiKeyDisplay.classList.remove('hidden');
            dom.getApiKeyBtn.classList.add('hidden');
            dom.copyApiKeyBtn.classList.remove('hidden');
            dom.rotateApiKeyBtn.classList.remove('hidden');
        }
    } catch (error) {
        showError(error);
    }
}

function handleCopyApiKey() {
    const key = dom.apiKeyDisplay.textContent;
    if (key) {
        navigator.clipboard.writeText(key).then(() => {
            const originalText = dom.copyApiKeyBtn.textContent;
            dom.copyApiKeyBtn.textContent = 'Copied!';
            setTimeout(() => { dom.copyApiKeyBtn.textContent = originalText; }, 2000);
        }).catch(err => showError({ message: 'Failed to copy API key.' }));
    }
}

function handleRotateApiKey() {
    dom.rotateKeyConfirmModal.classList.add('show');
}

async function executeRotateApiKey() {
    try {
        const newKey = await apiFetch('/api/v1/me/apikey/rotate', { method: 'POST' });
        if (newKey && newKey.key) {
            dom.apiKeyDisplay.textContent = newKey.key;
            dom.rotateKeyConfirmModal.classList.remove('show');
        }
    } catch (error) {
        showError(error);
        dom.rotateKeyConfirmModal.classList.remove('show');
    }
}

export function handleTrimClick(e) {
    const trackId = e.currentTarget.dataset.trackId;
    const pointTime = e.currentTarget.dataset.pointTime;

    const track = trackDataCache[trackId];
    if (!track) return showError({ message: "Could not find track data to trim." });

    const pointIndex = track.points.findIndex(p => p.time === pointTime);
    if (pointIndex === -1) return showError({ message: "Could not find the selected point in the track." });

    const toStart = pointIndex < track.points.length / 2;
    const trimPart = toStart ? 'end' : 'start';

    dom.trimConfirmMessage.innerHTML = `Are you sure you want to trim the ${trimPart} of the track from this point? This action cannot be undone.`;
    dom.trimConfirmButton.dataset.trackId = trackId;
    dom.trimConfirmButton.dataset.pointTime = pointTime;
    dom.trimConfirmButton.dataset.toStart = toStart;
    dom.trimConfirmModal.classList.add("show");
}

export function executeTrim(e) {
    const { trackId, pointTime, toStart } = e.currentTarget.dataset;
    apiFetch(`${apiUrl}/${trackId}/trim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ timestamp: pointTime, to_start: toStart === 'true' }),
    }).then(() => {
        dom.trimConfirmModal.classList.remove("show");
        if (trimPopup) trimPopup.remove();
        return apiFetch(`${apiUrl}/${trackId}?with_points=true`);
    }).then(updatedTrack => {
        trackDataCache[trackId] = updatedTrack;
        removeTrackFromMap(trackId);
        drawTrackOnMap(updatedTrack);
        refreshUI();
    }).catch(error => {
        dom.trimConfirmModal.classList.remove("show");
        showError(error);
    });
}

export function handleDeletePointClick(e) {
    const trackId = e.currentTarget.dataset.trackId;
    const pointId = e.currentTarget.dataset.pointId;

    dom.deletePointConfirmMessage.innerHTML = `Are you sure you want to delete this point? This action cannot be undone.`;
    dom.deletePointConfirmButton.dataset.trackId = trackId;
    dom.deletePointConfirmButton.dataset.pointId = pointId;
    dom.deletePointConfirmModal.classList.add("show");
}

export function executeDeletePoint(e) {
    const { trackId, pointId } = e.currentTarget.dataset;
    apiFetch(`/api/v1/point/${pointId}`, {
        method: 'DELETE',
    }).then(() => {
        dom.deletePointConfirmModal.classList.remove("show");
        if (trimPopup) trimPopup.remove();
        // Fetch the updated track to get new stats
        return apiFetch(`${apiUrl}/${trackId}?with_points=true`);
    }).then(updatedTrack => {
        // Update local cache
        trackDataCache[trackId] = updatedTrack;

        // Update map sources
        const lineSource = mapInstance.getSource(`line-source-${trackId}`);
        if (lineSource) {
            lineSource.setData({
                type: 'Feature',
                geometry: {
                    type: 'LineString',
                    coordinates: updatedTrack.points.map(p => [p.lon, p.lat])
                }
            });
        }

        const pointSource = mapInstance.getSource(`points-source-${trackId}`);
        if (pointSource) {
            pointSource.setData({
                type: 'FeatureCollection',
                features: updatedTrack.points.map(p => ({
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: [p.lon, p.lat]
                    },
                    properties: {
                        time: p.time,
                        id: p.id,
                        speed: p.speed.toFixed(1),
                        color: updatedTrack.color,
                        trackId: updatedTrack.id,
                        timeZone: updatedTrack.weather_report.time_zone
                    }
                }))
            });
        }

        // Update the legend
        updateLegend();
    }).catch(error => {
        dom.deletePointConfirmModal.classList.remove("show");
        showError(error);
    });
}

export function handleEditClick() {
    if (isDrawingMode) {
        const features = draw.getAll().features;
        saveTrack(features);
        draw.deleteAll();
        exitEditMode();
    } else {
        enterEditMode();
    }
}

export function showTracks(trackIds) {
    clearAllSelections();
    const promises = trackIds.map(id => apiFetch(`${apiUrl}/${id}?with_points=true`));

    Promise.all(promises).then(async tracks => {
        for (const track of tracks) {
            const trackId = track.id.toString();
            visibleTrackIds.add(trackId);
            trackDataCache[trackId] = track;
            await drawTrackOnMap(track);
            getPhotosForTrack(trackId).then(photos => {
                if (photos && photos.length > 0) {
                    drawPhotoIcons(trackId, photos);
                }
            });
            getNotesForTrack(trackId).then(notes => {
                if (notes && notes.length > 0) {
                    notesByTrackId[trackId] = notes;
                    drawNoteIcons(trackId, notes);
                }
            });

            const trackToggle = document.getElementById(`track-toggle-${trackId}`);
            if (trackToggle) {
                trackToggle.checked = true;
                trackToggle.closest('li').classList.add('current');
            }
        }
        refreshUI();
    }).catch(error => {
        showError(error);
    });
}

export function showTrip(tripId) {
    clearAllSelections();
    const tripListItem = document.querySelector(`.trip-list-item[data-trip-id='${tripId}']`);
    if (tripListItem) {
        tripListItem.click();
    } else {
        // If the trip is not in the list, fetch it directly
        apiFetch(`/api/v1/trip/${tripId}`).then(tripData => {
            if (!tripData || !tripData.tracks || tripData.tracks.length === 0) {
                updateLegend();
                return;
            }
            setActiveTrip(tripData);

            const drawPromises = tripData.tracks.map(async (track, index) => {
                const trackIdStr = track.id.toString();
                visibleTrackIds.add(trackIdStr);
                track.color = defaultTrackColors[index % defaultTrackColors.length];
                trackDataCache[trackIdStr] = track;
                await drawTrackOnMap(track);
                getPhotosForTrack(trackIdStr).then(photos => {
                    if (photos && photos.length > 0) {
                        drawPhotoIcons(trackIdStr, photos);
                    }
                });
                getNotesForTrack(trackIdStr).then(notes => {
                    if (notes && notes.length > 0) {
                        notesByTrackId[trackIdStr] = notes;
                        drawNoteIcons(trackIdStr, notes);
                    }
                });
            });

            Promise.all(drawPromises).then(() => {
                updateLegend();
                mapInstance.once('idle', () => {
                    updateMapBounds();
                    updateMarkersVisibility();
                });
            });
        }).catch(showError);
    }
}

function enterEditMode() {
    const [trackId] = visibleTrackIds;
    if (!trackId) return showError({ message: "Please select a single track to edit." });

    setDrawingMode(true);
    dom.editTrackButton.classList.add("active", "save");
    dom.editTrackButton.title = "Save changes";
    dom.editTrackButton.querySelector('.material-symbols-outlined').textContent = 'save';

    const track = trackDataCache[trackId];
    const lineString = {
        type: "Feature",
        geometry: {
            type: "LineString",
            coordinates: track.points.map(p => [p.lon, p.lat])
        }
    };
    const featureIds = draw.add(lineString);
    removeTrackFromMap(trackId);

    if (featureIds.length > 0) {
        draw.changeMode('direct_select', { featureId: featureIds[0] });
    }
}

function exitEditMode() {
    setDrawingMode(false);
    dom.editTrackButton.classList.remove("active", "save");
    dom.editTrackButton.title = "Edit this track";
    dom.editTrackButton.querySelector('.material-symbols-outlined').textContent = 'edit';
}





function saveTrack(features) {
    const [trackId] = visibleTrackIds;
    const originalTrack = trackDataCache[trackId];
    
    // For ReplacePoints, we send all points in the edited geometry.
    // We flatten multi-segment lines if any (Mapbox Draw usually returns a single LineString for a simple line)
    // but features can be multiple if multiple lines were drawn (though we usually edit one track).
    let newPoints = features.flatMap(feature => feature.geometry.coordinates);
    
    let newPointsFormatted = newPoints.map(point => ({ lat: point[1], lon: point[0] }));

    apiFetch(`${apiUrl}/${trackId}/points`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPointsFormatted),
    }).then(() => {
        return apiFetch(`${apiUrl}/${trackId}?with_points=true`);
    }).then(updatedTrack => {
        trackDataCache[trackId] = updatedTrack;
        removeTrackFromMap(trackId);
        drawTrackOnMap(updatedTrack);
        refreshUI();
    }).catch(error => {
        showError(error);
        removeTrackFromMap(trackId);
        drawTrackOnMap(originalTrack);
        refreshUI();
    });
}

function handleAddPhotoClick() {
    if (visibleTrackIds.size !== 1) {
        showError({ message: "Please select a single track to add a photo." });
        return;
    }
    dom.photoUploadInput.click();
}

async function handlePhotoUpload(e) {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const MAX_FILES = 20;
    const MAX_TOTAL_MB = 200;
    const MAX_FILE_MB = 50;

    // Client-side validation.
    if (files.length > MAX_FILES) {
        showError({ message: `Too many files selected: max ${MAX_FILES}.` });
        e.target.value = null;
        return;
    }
    let totalBytes = 0;
    for (const f of files) {
        if (f.size > MAX_FILE_MB * 1024 * 1024) {
            showError({ message: `File "${f.name}" exceeds the ${MAX_FILE_MB} MB per-file limit.` });
            e.target.value = null;
            return;
        }
        totalBytes += f.size;
    }
    if (totalBytes > MAX_TOTAL_MB * 1024 * 1024) {
        showError({ message: `Total upload size exceeds the ${MAX_TOTAL_MB} MB limit.` });
        e.target.value = null;
        return;
    }

    const formData = new FormData();
    for (const f of files) {
        formData.append('files', f);
    }

    const idempotencyKey = crypto.randomUUID();
    dom.addPhotoBtn.classList.add('disabled');

    if (dom.bulkUploadProgressModal) {
        if (dom.bulkUploadProgressMsg) dom.bulkUploadProgressMsg.textContent = `Processing ${files.length} photo${files.length !== 1 ? 's' : ''}…`;
        dom.bulkUploadProgressModal.classList.add('show');
    }

    try {
        const result = await bulkUploadPhotos(formData, idempotencyKey, null);
        showBulkUploadResults(result);

        // Refresh photos for any tracks that received uploads.
        const uploadedTrackIds = new Set(
            (result.results || [])
                .filter(r => r.status === 'uploaded' && r.track_id)
                .map(r => r.track_id)
        );
        for (const tid of uploadedTrackIds) {
            const photos = await getPhotosForTrack(tid);
            if (photos && photos.length > 0) drawPhotoIcons(tid, photos);
        }
    } catch (error) {
        showError(error);
    } finally {
        if (dom.bulkUploadProgressModal) dom.bulkUploadProgressModal.classList.remove('show');
        e.target.value = null;
        dom.addPhotoBtn.classList.remove('disabled');
    }
}

function showBulkUploadResults(result) {
    if (!dom.bulkUploadResultModal || !dom.bulkUploadResults) return;

    const results = result.results || [];
    const groups = {
        uploaded: results.filter(r => r.status === 'uploaded'),
        no_match: results.filter(r => r.status === 'no_match'),
        no_exif:  results.filter(r => r.status === 'no_exif'),
        duplicate: results.filter(r => r.status === 'duplicate'),
        error:    results.filter(r => r.status === 'error'),
    };

    const groupLabels = {
        uploaded:  { label: 'Uploaded', cls: 'bulk-group-success' },
        no_match:  { label: 'No matching track', cls: 'bulk-group-warning' },
        no_exif:   { label: 'No date in photo', cls: 'bulk-group-warning' },
        duplicate: { label: 'Duplicates', cls: 'bulk-group-muted' },
        error:     { label: 'Failed', cls: 'bulk-group-error' },
    };

    let html = `<p class="bulk-summary">${result.uploaded_count} of ${result.total_count} photos uploaded.</p>`;

    for (const [key, { label, cls }] of Object.entries(groupLabels)) {
        const items = groups[key];
        if (items.length === 0) continue;
        const collapsed = key === 'duplicate' ? ' collapsed' : '';
        html += `<div class="bulk-group ${cls}${collapsed}">`;
        html += `<div class="bulk-group-header">${label} (${items.length})</div>`;

        if (key === 'uploaded') {
            // Group uploaded photos by track name for clarity.
            const byTrack = new Map();
            for (const item of items) {
                const name = item.track_name || `Track ${item.track_id}`;
                if (!byTrack.has(name)) byTrack.set(name, []);
                byTrack.get(name).push(item.filename);
            }
            html += '<ul class="bulk-group-list">';
            for (const [trackName, filenames] of byTrack) {
                html += `<li><span class="bulk-track-name">${escapeHtml(trackName)}</span>`;
                html += ` <span class="bulk-reason">— ${filenames.length} photo${filenames.length !== 1 ? 's' : ''}</span></li>`;
            }
            html += '</ul>';
        } else {
            html += '<ul class="bulk-group-list">';
            for (const item of items) {
                html += `<li title="${item.reason || ''}">${escapeHtml(item.filename)}`;
                if (item.reason) html += ` <span class="bulk-reason">— ${escapeHtml(item.reason)}</span>`;
                html += '</li>';
            }
            html += '</ul>';
        }
        html += '</div>';
    }

    dom.bulkUploadResults.innerHTML = html;
    dom.bulkUploadResultModal.classList.add('show');
}

function escapeHtml(str) {
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

export function showNoteModal(pointId, note) {
    setTimeout(() => {
        const popups = document.querySelectorAll('.mapboxgl-popup');
        popups.forEach(p => p.remove());
    }, 0);

    const noteModal = document.getElementById('note-modal');
    const noteCaptionInput = noteModal.querySelector('.note-caption');
    const deleteNoteBtn = document.getElementById('delete-note-btn');
    const closeBtn = noteModal.querySelector('.close-button');

    noteCaptionInput.value = note ? note.content : '';
    noteCaptionInput.dataset.pointId = pointId;
    noteCaptionInput.dataset.noteId = note ? note.id : '';
    deleteNoteBtn.dataset.noteId = note ? note.id : '';

    // Handle Attribution
    let attributionEl = noteModal.querySelector('.note-attribution');
    if (!attributionEl) {
        attributionEl = document.createElement('div');
        attributionEl.className = 'note-attribution';
        // Insert after caption input (textarea)
        if (noteCaptionInput.nextSibling) {
            noteCaptionInput.parentNode.insertBefore(attributionEl, noteCaptionInput.nextSibling);
        } else {
            noteCaptionInput.parentNode.appendChild(attributionEl);
        }
    }
    attributionEl.textContent = (note && note.author_name) ? `— ${note.author_name}` : '';
    attributionEl.style.display = (note && note.author_name) ? 'block' : 'none';

    const isSharedView = dom.authControls.classList.contains('hidden');

    if (isSharedView) {
        noteCaptionInput.disabled = true;
        deleteNoteBtn.style.display = 'none';
    } else {
        noteCaptionInput.disabled = false; // Ideally check isMine for editing content, similar to photo caption
        // For now, allow editing if authenticated? No, should restrict to author.
        // note object has is_mine field now.
        // But JS here receives what `getNotesForTrack` returns.
        // Backend Go model has `IsMine`.
        
        // The delete button visibility based on CanDelete
        const canDelete = note ? note.can_delete : false;
        deleteNoteBtn.style.display = (note && canDelete) ? 'inline-block' : 'none';
    }

    noteModal.classList.remove('hidden');
    noteModal.classList.add('show');

    const close = () => {
        noteModal.classList.add('hidden');
        noteModal.classList.remove('show');
        noteCaptionInput.removeEventListener('blur', saveOnBlur);
    }

    const saveOnBlur = async () => {
        const content = noteCaptionInput.value;
        const pointId = noteCaptionInput.dataset.pointId;
        try {
            await saveNote(pointId, content);
            const [trackId] = visibleTrackIds;
            const notes = await getNotesForTrack(trackId) || [];
            notesByTrackId[trackId] = notes;
            drawNoteIcons(trackId, notes);
        } catch (error) {
            showError(error);
        }
    };

    noteCaptionInput.addEventListener('blur', saveOnBlur);
    closeBtn.onclick = close;

    deleteNoteBtn.onclick = async () => {
        const noteId = deleteNoteBtn.dataset.noteId;
        try {
            await deleteNote(noteId);
            close();
            const [trackId] = visibleTrackIds;
            const notes = await getNotesForTrack(trackId) || [];
            notesByTrackId[trackId] = notes;
            drawNoteIcons(trackId, notes);
        } catch (error) {
            showError(error);
        }
    };
}

export function showPhotoModal(signedUrl, photoId, caption, canDelete, authorName) {
    dom.photoModal.classList.add('loading');
    dom.photoModal.classList.remove('hidden');
    dom.photoModal.classList.add('show');
    dom.photoModalSpinner.classList.add('show');
    dom.photoModalSpinner.classList.remove('hidden');
    dom.photoModalImage.src = '/placeholder.svg';

    dom.photoModalImage.onload = () => {
        dom.photoModal.classList.remove('loading');
        dom.photoModalSpinner.classList.remove('show');
        dom.photoModalSpinner.classList.add('hidden');
        dom.photoModalImage.src = signedUrl;
    };
    
    // Handle Attribution
    let attributionEl = dom.photoModal.querySelector('.photo-attribution');
    if (!attributionEl) {
        attributionEl = document.createElement('div');
        attributionEl.className = 'photo-attribution';
        // Insert before the actions/caption container if possible, or at bottom
        const container = dom.photoModal.querySelector('.modal-content') || dom.photoModal;
        container.appendChild(attributionEl);
    }
    attributionEl.textContent = authorName ? `— ${authorName}` : '';
    attributionEl.style.display = authorName ? 'block' : 'none';


    dom.photoModalCaption.value = caption || '';

    // Remove any existing blur listener before deciding to add a new one
    if (captionBlurHandler) {
        dom.photoModalCaption.removeEventListener('blur', captionBlurHandler);
        captionBlurHandler = null;
    }

    const close = () => {
        const popups = document.querySelectorAll('.mapboxgl-popup-anchor-bottom');
        popups.forEach(popup => popup.remove());
        dom.photoModal.classList.add('hidden');
        dom.photoModal.classList.remove('show');
        dom.photoModal.classList.remove('loading');
        dom.photoModalImage.src = '/placeholder.svg';
        // Also remove listener on close
        if (captionBlurHandler) {
            dom.photoModalCaption.removeEventListener('blur', captionBlurHandler);
            captionBlurHandler = null;
        }
        // Remove the click outside listener
        dom.photoModal.removeEventListener('click', clickOutsideHandler);
    }

    const clickOutsideHandler = (e) => {
        if (e.target === dom.photoModal) {
            close();
        }
    };

    dom.photoModal.addEventListener('click', clickOutsideHandler);

    dom.photoModalCloseBtn.onclick = close;

    const isSharedView = dom.authControls.classList.contains('hidden');

    // Determine if user can edit/delete
    // We allow editing caption if it's your photo.
    // Deletion depends on canDelete (passed from backend logic).
    // Actually, updatePhotoCaption also checks permission (author only? or track owner too? DB says: WHERE id = $2 AND person_id = $3. So only author can update caption).
    // So we need to know if I am the author for caption editing.
    // canDelete is true if I am author OR track owner.
    // We don't explicitly pass isMine. But typically authorName being "Me" or matching current user would indicate.
    // However, the backend logic for UpdatePhotoCaption restricts to person_id.
    // Let's assume for now only Author can Edit Caption.
    // Since we don't have 'isMine' passed here explicitly, we might need it.
    // But `canDelete` covers deletion.
    // Let's check if we passed isMine?
    // The datastore query returns `is_mine` in the JSON.
    // In `map.js`, we put `can_delete` and `author_name`. We missed `is_mine`.

    if (isSharedView) {
        dom.photoModalCaption.placeholder = '';
        dom.photoModalCaption.disabled = true;
        dom.photoModalDeleteBtn.style.display = 'none';
    } else {
        // For now, enable caption editing if canDelete is true? No, track owner can delete but maybe not edit caption of others?
        // The DB `UpdatePhotoCaption` requires `person_id` match. So only Author.
        // I should pass `isMine` too.
        // For this iteration, I'll stick to what I have. If `canDelete` is true, I'll show delete button.
        
        dom.photoModalCaption.placeholder = 'Add a caption...';
        dom.photoModalCaption.disabled = false; // Ideally check isMine
        dom.photoModalDeleteBtn.style.display = canDelete ? 'block' : 'none';

        captionBlurHandler = async () => {
            const newCaption = dom.photoModalCaption.value;
            try {
                await updatePhotoCaption(photoId, newCaption);
                const [trackId] = visibleTrackIds;
                const photos = await getPhotosForTrack(trackId);
                drawPhotoIcons(trackId, photos);
            } catch (error) {
                // If we fail (e.g. because we are track owner but not photo author), show error
               // showError(error);
               // Actually, silently fail or log might be better UI if we can't distinguish permissions here yet.
               console.warn("Failed to update caption", error);
            }
        };
        dom.photoModalCaption.addEventListener('blur', captionBlurHandler);

        dom.photoModalDeleteBtn.onclick = async () => {
            if (confirm('Are you sure you want to delete this photo?')) {
                try {
                    await deletePhoto(photoId);
                    close();
                    const [trackId] = visibleTrackIds;
                    const photos = await getPhotosForTrack(trackId);
                    drawPhotoIcons(trackId, photos);
                } catch (error) {
                    showError(error);
                }
            }
        };
    }
                   
}

export function initializeEventListeners() {
    if (dom.errorButton) dom.errorButton.addEventListener("click", hideError);
    if (dom.uploadForm) dom.uploadForm.addEventListener("submit", processUpload);
    if (dom.uploadCancelButton) dom.uploadCancelButton.addEventListener("click", hideUpload);
    if (dom.uploadFileInput) dom.uploadFileInput.addEventListener("change", enableUpload);
    if (dom.legendDescriptionText) dom.legendDescriptionText.addEventListener("blur", saveTrackDetails);
    if (dom.legendTitle) dom.legendTitle.addEventListener("blur", saveTrackDetails);
    if (dom.deleteConfirmButton) dom.deleteConfirmButton.addEventListener("click", executeDelete);
    if (dom.deleteCancelButton) dom.deleteCancelButton.addEventListener("click", hideDeleteConfirm);
    if (dom.deletePointConfirmButton) dom.deletePointConfirmButton.addEventListener("click", executeDeletePoint);
    if (dom.deletePointCancelButton) dom.deletePointCancelButton.addEventListener("click", () => dom.deletePointConfirmModal.classList.remove("show"));
    if (dom.trimConfirmButton) dom.trimConfirmButton.addEventListener("click", executeTrim);
    if (dom.trimCancelButton) dom.trimCancelButton.addEventListener("click", () => dom.trimConfirmModal.classList.remove("show"));
    if (dom.addPhotoBtn) dom.addPhotoBtn.addEventListener('click', handleAddPhotoClick);
    if (dom.photoUploadInput) dom.photoUploadInput.addEventListener('change', handlePhotoUpload);
    if (dom.bulkUploadOkBtn) dom.bulkUploadOkBtn.addEventListener('click', () => dom.bulkUploadResultModal.classList.remove('show'));
    if (dom.exportBtn) dom.exportBtn.addEventListener('click', captureAndExport);
    if (dom.importForm) dom.importForm.addEventListener('submit', processImport);
    if (dom.importFileInput) dom.importFileInput.addEventListener('change', enableImportUpload);
    const importCancelBtn = dom.importUI ? dom.importUI.querySelector('button.cancel') : null;
    if (importCancelBtn) importCancelBtn.addEventListener('click', hideImport);
    if (dom.importOkBtn) dom.importOkBtn.addEventListener('click', hideImport);
    if (dom.toggleMarkersBtn) dom.toggleMarkersBtn.addEventListener('click', handleToggleMarkers);

    const toggleBtn = document.querySelector('.toggle-legend-btn');
    if (toggleBtn) {
        toggleBtn.addEventListener("click", toggleLegend);
    }

    if (dom.toggleTracksButton) dom.toggleTracksButton.addEventListener("click", toggleTracksPanel);
    if (dom.shareTrackButton) dom.shareTrackButton.addEventListener("click", handleShareClick);
    if (dom.editTrackButton) dom.editTrackButton.addEventListener("click", handleEditClick);
    if (dom.header) makeDraggable(dom.header);
    const compactToggleButton = document.querySelector('.compact-toggle-btn');
    if (compactToggleButton) {
        compactToggleButton.addEventListener('click', () => {
            const header = document.querySelector('header.legend');
            header.classList.toggle('compact');
        });
    }
    if (dom.closeShareModalBtn) dom.closeShareModalBtn.addEventListener('click', () => dom.shareModal.classList.remove('show'));
    if (dom.copyShareUrlBtn) dom.copyShareUrlBtn.addEventListener('click', () => {
        navigator.clipboard.writeText(dom.shareUrlInput.value).then(() => {
            const icon = dom.copyShareUrlBtn.querySelector('.material-symbols-outlined');
            const originalText = icon.textContent;
            icon.textContent = 'check';
            setTimeout(() => { icon.textContent = originalText; }, 2000);
        }).catch(err => {
            showError({ message: 'Failed to copy URL to clipboard.' });
            console.error('Clipboard error:', err);
        });
    });

    const legendToggleButton = document.querySelector('.legend-toggle-button');
    if (legendToggleButton) {
        legendToggleButton.addEventListener('click', () => {
            const header = document.querySelector('header.legend');
            header.classList.toggle('collapsed');
            const icon = legendToggleButton.querySelector('.material-symbols-outlined');
            icon.textContent = header.classList.contains('collapsed') ? 'unfold_more' : 'unfold_less';
        });
    }

    const statsContainer = document.getElementById('stats-container');
    if (statsContainer) {
        const closeButton = statsContainer.querySelector('.close-stats-btn');
        if (closeButton) {
            closeButton.addEventListener('click', () => {
                statsContainer.classList.remove('visible');
                const showStatsBtn = document.getElementById('show-stats-mobile-btn');
                if (showStatsBtn) {
                    showStatsBtn.textContent = 'Show Stats';
                }
            });
        }
    }

    dom.tabs.forEach(tab => {
        tab.addEventListener('click', () => {
            dom.tabs.forEach(t => t.classList.remove('active'));
            dom.tabContents.forEach(c => c.classList.remove('active'));
            tab.classList.add('active');
            document.getElementById(tab.dataset.tab).classList.add('active');
            updateActionButtons(tab.dataset.tab);
        });
    });

    if (dom.boatForm) {
        dom.boatForm.addEventListener('submit', handleBoatFormSubmit);
        dom.boatForm.querySelector('.cancel').addEventListener('click', () => {
            dom.boatFormModal.classList.remove('show');
            history.pushState(null, '', previousUrl);
        });
        initDiagram(dom.boatForm, document.getElementById('svg_container'));
        ['boatlength', 'beam', 'draft', 'mast'].forEach(fieldName => {
            dom.boatForm.elements[fieldName].addEventListener('input', e => validateBoatSizeField(e.target));
        });

        const typeToggle = document.getElementById('boat-type-toggle');
        if (typeToggle) {
            typeToggle.addEventListener('change', () => {
                const hiddenType = dom.boatForm.elements.type;
                if (hiddenType) {
                    hiddenType.value = typeToggle.checked ? 'Catamaran' : 'Monohull';
                    syncTypeToggle(hiddenType.value);
                    hiddenType.dispatchEvent(new Event('change'));
                }
            });

            const labelMono = document.getElementById('type-label-monohull');
            const labelCat = document.getElementById('type-label-catamaran');
            if (labelMono) {
                labelMono.addEventListener('click', () => {
                    if (typeToggle.checked) {
                        typeToggle.checked = false;
                        typeToggle.dispatchEvent(new Event('change'));
                    }
                });
            }
            if (labelCat) {
                labelCat.addEventListener('click', () => {
                    if (!typeToggle.checked) {
                        typeToggle.checked = true;
                        typeToggle.dispatchEvent(new Event('change'));
                    }
                });
            }
        }

        if (dom.boatAgentBtn) {
            const fields = ['name', 'make', 'model'];
            fields.forEach(fieldName => {
                const input = dom.boatForm.elements[fieldName];
                if (input) {
                    input.addEventListener('input', () => {
                        const name = dom.boatForm.elements.name.value.trim();
                        const make = dom.boatForm.elements.make.value.trim();
                        const model = dom.boatForm.elements.model.value.trim();
                        dom.boatAgentBtn.classList.toggle('disabled', !(name && (make || model)));
                    });
                }
            });
            dom.boatAgentBtn.addEventListener('click', handleBoatAgentSearch);
        }
            if (dom.boatAgentConfirmBtn) dom.boatAgentConfirmBtn.addEventListener('click', handleBoatAgentConfirm);
            if (dom.boatAgentCancelBtn) dom.boatAgentCancelBtn.addEventListener('click', () => dom.boatAgentConfirmModal.classList.remove('show'));
        
            const boatViewCancelBtn = document.querySelector('.boat-view-modal .cancel');
            if (boatViewCancelBtn) boatViewCancelBtn.addEventListener('click', () => dom.boatViewModal.classList.remove('show'));
        }
    if (dom.userAvatar) dom.userAvatar.addEventListener('click', showPersonModal);
    if (dom.editPersonNameBtn) dom.editPersonNameBtn.addEventListener('click', handleEditPersonNameClick);
    if (dom.savePersonNameBtn) dom.savePersonNameBtn.addEventListener('click', handleSavePersonNameClick);
    if (dom.cancelPersonNameBtn) dom.cancelPersonNameBtn.addEventListener('click', handleCancelPersonNameClick);
    if (dom.closePersonModalBtn) dom.closePersonModalBtn.addEventListener('click', () => dom.personModal.classList.remove('show'));
    const profileImportBtn = document.getElementById('profile-import-btn');
    if (profileImportBtn) profileImportBtn.addEventListener('click', (e) => {
        dom.personModal.classList.remove('show');
        addImport(e);
    });
    const profileExportBtn = document.getElementById('profile-export-btn');
    if (profileExportBtn) {
        profileExportBtn.addEventListener('click', () => {
            window.location.href = '/api/v1/me/export';
        });
    }
        if (dom.getApiKeyBtn) {
        dom.getApiKeyBtn.addEventListener('click', handleGetApiKey);
    }
    if (dom.copyApiKeyBtn) dom.copyApiKeyBtn.addEventListener('click', handleCopyApiKey);
    if (dom.rotateApiKeyBtn) dom.rotateApiKeyBtn.addEventListener('click', handleRotateApiKey);
    if (dom.rotateKeyConfirmButton) dom.rotateKeyConfirmButton.addEventListener('click', executeRotateApiKey);
    if (dom.rotateKeyCancelButton) dom.rotateKeyCancelButton.addEventListener('click', () => dom.rotateKeyConfirmModal.classList.remove('show'));

    const showLegendBtn = document.getElementById('show-legend-mobile-btn');
    if (showLegendBtn) {
        showLegendBtn.addEventListener('click', () => {
            const legend = document.querySelector('.legend');
            if (legend) {
                const isFullScreen = legend.classList.toggle('mobile-fullscreen');
                legend.classList.toggle('mobile-hidden', !isFullScreen);
                showLegendBtn.textContent = isFullScreen ? 'Show Map' : 'Show Details';
            }
        });
    }

    const showStatsBtn = document.getElementById('show-stats-mobile-btn');
    if (showStatsBtn) {
        showStatsBtn.addEventListener('click', () => {
            const statsContainer = document.getElementById('stats-container');
            if (statsContainer) {
                const isVisible = statsContainer.classList.toggle('visible');
                statsContainer.classList.toggle('hidden', !isVisible);
                showStatsBtn.textContent = isVisible ? 'Hide Stats' : 'Show Stats';
            }
        });
    }
    
    // Getting Started Modal Listeners
    if (dom.gsUploadTrackBtn) {
        dom.gsUploadTrackBtn.addEventListener('click', () => {
            dom.gettingStartedModal.classList.remove('show');
            addTrack({ preventDefault: () => {} }); // Reuse existing function
        });
    }

    if (dom.gsAddBoatBtn) {
        dom.gsAddBoatBtn.addEventListener('click', () => {
            dom.gettingStartedModal.classList.remove('show');
            showBoatForm();
        });
    }

    if (dom.gsCloseBtn) {
        dom.gsCloseBtn.addEventListener('click', () => {
            dom.gettingStartedModal.classList.remove('show');
            localStorage.setItem('gettingStartedDismissed', 'true');
        });
    }

    // Crew Modal Listeners
    if (dom.crewTabs) {
        dom.crewTabs.forEach(tab => tab.addEventListener('click', handleCrewTabClick));
    }
    if (dom.addCrewForm) dom.addCrewForm.addEventListener('submit', handleAddCrewSubmit);
    if (dom.addCrewTypeRadios) {
        dom.addCrewTypeRadios.forEach(radio => radio.addEventListener('change', handleAddCrewTypeChange));
    }
    if (dom.closeCrewModalBtn) {
        dom.closeCrewModalBtn.addEventListener('click', () => dom.crewModal.classList.remove('show'));
    }

    // Edit Crew Role Modal Listeners
    if (dom.editCrewRoleForm) {
        dom.editCrewRoleForm.addEventListener('submit', handleEditCrewRoleSubmit);
        dom.editCrewRoleForm.querySelector('.cancel').addEventListener('click', () => {
            dom.editCrewRoleModal.classList.remove('show');
        });
    }

    // Credential Modal Listeners
    const manageCredentialsBtn = document.getElementById('manage-credentials-btn');
    if (manageCredentialsBtn) {
        manageCredentialsBtn.addEventListener('click', () => {
            dom.personModal.classList.remove('show');
            openCredentialModal();
        });
    }
    if (dom.closeCredModalBtn) {
        dom.closeCredModalBtn.addEventListener('click', () => dom.credentialModal.classList.remove('show'));
    }
    if (dom.credTabs) {
        dom.credTabs.forEach(tab => tab.addEventListener('click', e => {
            const targetId = e.currentTarget.dataset.tab;
            dom.credTabs.forEach(t => t.classList.remove('active'));
            dom.credTabContents.forEach(c => c.classList.remove('active'));
            e.currentTarget.classList.add('active');
            document.getElementById(targetId).classList.add('active');
        }));
    }
    if (dom.credentialModal) {
        dom.credentialModal.querySelectorAll('.add-cred-btn').forEach(btn => {
            btn.addEventListener('click', () => showCredentialForm(btn.dataset.kind));
        });
    }
    if (dom.credForm) dom.credForm.addEventListener('submit', handleCredentialFormSubmit);
    const cancelCredFormBtn = document.querySelector('.cancel-cred-form-btn');
    if (cancelCredFormBtn) cancelCredFormBtn.addEventListener('click', hideCredentialForm);

    window.addEventListener('popstate', handleUrlPath);
}

async function openCrewModal(trackId) {
    if (!trackId) return;
    dom.crewModal.dataset.trackId = trackId;
    dom.crewModal.classList.add('show');
    renderCrewList(trackId);
}

function handleCrewTabClick(e) {
    const tab = e.currentTarget;
    const targetId = tab.dataset.tab;
    
    dom.crewTabs.forEach(t => t.classList.remove('active'));
    dom.crewTabContents.forEach(c => c.classList.remove('active'));
    
    tab.classList.add('active');
    document.getElementById(targetId).classList.add('active');
}

function handleAddCrewTypeChange(e) {
    const type = e.target.value;
    if (type === 'search') {
        dom.searchModeGroup.classList.remove('hidden');
        dom.guestModeGroup.classList.add('hidden');
    } else {
        dom.searchModeGroup.classList.add('hidden');
        dom.guestModeGroup.classList.remove('hidden');
    }
}

async function renderCrewList(trackId) {
    if (!dom.crewList) return;
    dom.crewList.innerHTML = '<li class="loader-item"><div class="loader"></div></li>';
    
    const track = trackDataCache[trackId];
    const isOwner = currentUser && track && currentUser.id === track.owner_id;

    // Toggle visibility of "Add Crew" tab based on ownership
    const addCrewTab = document.querySelector('.crew-tab-button[data-tab="add-crew"]');
    if (addCrewTab) {
        if (isOwner) {
            addCrewTab.style.display = '';
        } else {
            addCrewTab.style.display = 'none';
            // Ensure we are on the list tab if add tab is hidden
            if (addCrewTab.classList.contains('active')) {
                document.querySelector('.crew-tab-button[data-tab="current-crew"]').click();
            }
        }
    }

    try {
        const crew = await listTrackCrew(trackId);
        dom.crewList.innerHTML = '';
        
        if (!crew || crew.length === 0) {
            dom.crewList.innerHTML = '<li class="empty-item">No crew members assigned yet.</li>';
            return;
        }

        crew.forEach(member => {
            const li = document.createElement('li');
            li.className = 'crew-item';
            
            const avatarSrc = member.picture_url || '/profile_placeholder.svg'; // Use a generic avatar or favicon
            
            let actionsHtml = '';
            if (isOwner) {
                actionsHtml = `
                <div class="crew-actions">
                    <button class="edit-role-btn button-base small" data-id="${member.id}" data-name="${member.name}" data-role="${member.role}" title="Edit Role"><span class="material-symbols-outlined">edit</span></button>
                    <button class="remove-crew-btn button-danger small" data-id="${member.id}" title="Remove"><span class="material-symbols-outlined">delete</span></button>
                </div>`;
            }

            li.innerHTML = `
                <div class="crew-info">
                    <img src="${avatarSrc}" alt="${member.name}" class="crew-avatar">
                    <div class="crew-details">
                        <span class="crew-name">${member.name}</span>
                        <span class="crew-role">${member.role}</span>
                    </div>
                </div>
                ${actionsHtml}
            `;
            
            if (isOwner) {
                li.querySelector('.edit-role-btn').addEventListener('click', handleUpdateCrewRole);
                li.querySelector('.remove-crew-btn').addEventListener('click', handleRemoveCrewClick);
            }
            
            dom.crewList.appendChild(li);
        });

    } catch (error) {
        showError(error);
        dom.crewList.innerHTML = '<li class="error-item">Failed to load crew.</li>';
    }
}

function handleRemoveCrewClick(e) {
    const btn = e.currentTarget;
    const crewId = btn.dataset.id;
    const trackId = dom.crewModal.dataset.trackId;
    
    if (confirm('Are you sure you want to remove this crew member?')) {
        removeCrew(trackId, crewId).then(() => {
            renderCrewList(trackId);
            loadAndRenderTrackCrewHeader(trackId);
        }).catch(showError);
    }
}

function handleUpdateCrewRole(e) {
    const btn = e.currentTarget;
    const crewId = btn.dataset.id;
    const currentRole = btn.dataset.role;
    const currentName = btn.dataset.name;
    const trackId = dom.crewModal.dataset.trackId;
    
    dom.editCrewTrackId.value = trackId;
    dom.editCrewMemberId.value = crewId;
    dom.editCrewMemberName.value = currentName;
    dom.editCrewRoleInput.value = currentRole;
    
    dom.editCrewRoleModal.classList.add('show');
}

async function handleEditCrewRoleSubmit(e) {
    e.preventDefault();
    const trackId = dom.editCrewTrackId.value;
    const crewId = dom.editCrewMemberId.value;
    const name = dom.editCrewMemberName.value;
    const role = dom.editCrewRoleInput.value;

    try {
        await updateCrew(trackId, crewId, { name, role });
        dom.editCrewRoleModal.classList.remove('show');
        renderCrewList(trackId);
        loadAndRenderTrackCrewHeader(trackId);
    } catch (error) {
        showError(error);
    }
}


async function handleAddCrewSubmit(e) {
    e.preventDefault();
    const trackId = dom.crewModal.dataset.trackId;
    const type = document.querySelector('input[name="add-crew-type"]:checked').value;
    const role = dom.crewRoleInput.value;
    
    const data = { role };
    
    if (type === 'search') {
        data.email = dom.crewEmailInput.value;
        if (!data.email) return showError({ message: "Please enter an email address." });
    } else {
        data.name = dom.crewNameInput.value;
        if (!data.name) return showError({ message: "Please enter a name." });
    }
    
    try {
        await addCrew(trackId, data);
        dom.addCrewForm.reset();
        // Switch back to list tab
        document.querySelector('.crew-tab-button[data-tab="current-crew"]').click();
        renderCrewList(trackId);
        loadAndRenderTrackCrewHeader(trackId);
    } catch (error) {
        showError(error);
    }
}

async function loadAndRenderTripCrewHeader(tripId) {
    const container = dom.multiTrackView.querySelector('.trip-crew-summary');
    if (!container) return;
    
    try {
        const crew = await listTripCrew(tripId);
        if (crew && crew.length > 0) {
            const skippers = crew.filter(c => c.role === 'Skipper'); // Could be multiple if from diff tracks
            const others = crew.filter(c => c.role !== 'Skipper');
            
            let html = '';
            if (skippers.length > 0) {
                // Deduplicate skippers by name or person_id just in case, though DB query likely handles distinct
                const uniqueSkippers = [...new Map(skippers.map(item => [item.name, item])).values()];
                html += `<div class="skipper-display"><span class="material-symbols-outlined">local_police</span> <span>${uniqueSkippers.map(s => s.name).join(', ')}</span></div>`;
            }
            
            if (others.length > 0) {
                html += `<div class="crew-avatars-list">`;
                others.forEach(c => {
                     const avatarSrc = c.picture_url || '/profile_placeholder.svg';
                     html += `<img src="${avatarSrc}" alt="${c.name}" title="${c.name} (${c.role})" class="mini-avatar">`;
                });
                html += `</div>`;
            }
            container.innerHTML = html;
        } else {
            container.innerHTML = '';
        }
    } catch (e) {
        console.warn('Failed to load trip crew for header', e);
    }
}

async function loadAndRenderTrackCrewHeader(trackId) {
    // We need a container in the single track view for crew. 
    // Let's assume one exists or we inject it into '.legend-description-container' or a new place.
    // Looking at renderLegendSingleTrackView, there isn't a dedicated slot yet.
    // Let's append it to the title/date block or create a new block.
    
    let container = dom.singleTrackView.querySelector('.track-crew-summary');
    if (!container) {
        // Create if not exists, insert after date
        container = document.createElement('div');
        container.className = 'track-crew-summary';
        const dateEl = dom.singleTrackView.querySelector('.legend-date');
        if (dateEl && dateEl.parentNode) {
            dateEl.parentNode.insertBefore(container, dateEl.nextSibling);
        }
    }
    
    try {
        const crew = await listTrackCrew(trackId);
        if (crew && crew.length > 0) {
            const skipper = crew.find(c => c.role === 'Skipper');
            const others = crew.filter(c => c.role !== 'Skipper');
            
            let html = '';
            if (skipper) {
                html += `<div class="skipper-display"><span class="material-symbols-outlined">local_police</span> <span>${skipper.name}</span></div>`;
            }
            
            if (others.length > 0) {
                html += `<div class="crew-avatars-list">`;
                others.forEach(c => {
                     const avatarSrc = c.picture_url || '/profile_placeholder.svg';
                     html += `<img src="${avatarSrc}" alt="${c.name}" title="${c.name} (${c.role})" class="mini-avatar">`;
                });
                html += `</div>`;
            }
            container.innerHTML = html;
            container.style.display = 'flex';
        } else {
            container.innerHTML = '';
            container.style.display = 'none';
        }
    } catch (e) {
        console.warn('Failed to load track crew for header', e);
    }
}

